// ASSIGNMENTS_SINGLE_SCROLL_WORKSPACE_V1
import { useState, useEffect, useRef } from 'react';
// TRADITIONAL_CORE_WEIGHTS_PATCH_20260813
// TRADITIONAL_GRADING_PATCH_20260813
import RichTextEditor from '../../../components/RichTextEditor';
import GradingPdfViewer from './GradingPdfViewer';
import MultiFileGradingViewer from './MultiFileGradingViewer';
import InstructorAssignmentUpload from './InstructorAssignmentUpload';
import ReceivedPeerReviews from '../../student/course/ReceivedPeerReviews';
import StudentProgress from './StudentProgress';
import {
  SELF_EVAL_REVIEW_KEY,
  selfEvalReviewIsComplete,
  selfEvalCompleteGradeLabel
} from '../../../utils/selfEvalReview';
import { notifyReleasedIncomplete } from '../../../utils/incompleteGradeNotifications';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchSelfEvalQuestions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_questions?active=eq.true&select=*&order=question_order.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  return response.json();
}

async function fetchCourseSelfEvalQuestion(courseId, section) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_self_evaluation_questions?course_id=eq.${courseId}&section=eq.${encodeURIComponent(section)}&select=*`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  const data = await response.json();
  return data[0] || null;
}

async function fetchInstructorSubmissionFeedback(
  submissionType,
  submissionId
) {
  if (
    !submissionType ||
    !submissionId
  ) {
    return [];
  }

  /*
   * PD feedback keeps the existing reader exactly as before.
   * Only assignment grading needs the unpublished overlay.
   */
  if (submissionType !== 'assignment') {
    const response =
      await fetch(
        SUPABASE_URL +
          '/rest/v1/instructor_submission_feedback' +
          '?submission_type=eq.' +
          encodeURIComponent(
            submissionType
          ) +
          '&submission_id=eq.' +
          encodeURIComponent(
            submissionId
          ) +
          '&select=*,instructors(id,name,email)' +
          '&order=created_at.asc',
        {
          headers: {
            'apikey':
              SUPABASE_KEY,

            'Authorization':
              'Bearer ' +
              SUPABASE_KEY
          }
        }
      );

    if (!response.ok) {
      const detail =
        await response.text();

      throw new Error(
        'Loading instructor feedback failed (' +
          response.status +
          '): ' +
          detail
      );
    }

    const rows =
      await response.json();

    return Array.isArray(rows)
      ? rows
      : [];
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/get_instructor_submission_feedback_for_grading',
      {
        method: 'POST',
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          p_submission_type:
            'assignment',
          p_submission_ids: [
            submissionId
          ]
        })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Loading assignment grading failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  return (
    Array.isArray(rows)
      ? rows
      : []
  ).map(row => ({
    ...row,
    instructors: {
      id: row.instructor_id,
      name:
        row.instructor_name ||
        '',
      email:
        row.instructor_email ||
        ''
    }
  }));
}

async function saveInstructorSubmissionFeedback(
  submissionType,
  submissionId,
  instructorId,
  data
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/save_instructor_submission_feedback',
      {
        method: 'POST',

        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            p_submission_type:
              submissionType,

            p_submission_id:
              submissionId,

            p_instructor_id:
              instructorId,

            p_comment:
              data.comment || null,

            p_instructor_note:
              data.instructor_note ||
              null,

            p_completion_status:
              data.completion_status ||
              null,

            p_score:
              data.score === '' ||
              data.score === undefined
                ? null
                : data.score,

            p_submitted:
              data.submitted === true,

            p_submitted_at:
              data.submitted_at ||
              null
          })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Saving instructor feedback failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  return response.json();
}

async function saveUnpublishedInstructorSubmissionFeedback(
  submissionId,
  instructorId,
  data
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/save_unpublished_instructor_submission_feedback',
      {
        method: 'POST',

        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            p_submission_id:
              submissionId,

            p_instructor_id:
              instructorId,

            p_comment:
              data.comment || null,

            p_instructor_note:
              data.instructor_note ||
              null,

            p_completion_status:
              data.completion_status ||
              null,

            p_score:
              data.score === '' ||
              data.score === undefined
                ? null
                : data.score,

            p_submitted_at:
              data.submitted_at ||
              new Date()
                .toISOString()
          })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Saving unpublished grading failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  return response.json();
}


async function fetchSubmissionWithEffectiveFeedback(
  submissionType,
  submissionId
) {
  const isPd =
    submissionType === 'pd';

  const table =
    isPd
      ? 'pd_submissions'
      : 'assignment_submissions';

  const feedbackRelation =
    isPd
      ? 'pd_feedback'
      : 'assignment_feedback';

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/' +
        table +
        '?id=eq.' +
        encodeURIComponent(
          submissionId
        ) +
        '&select=*,' +
        feedbackRelation +
        '(*)&limit=1',
      {
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Refreshing effective feedback failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  return (
    Array.isArray(rows)
      ? rows[0] || null
      : null
  );
}



async function fetchAssignmentGroupGradeOverride(
  submissionId,
  enrolmentId
) {
  if (!submissionId || !enrolmentId) {
    return null;
  }

  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/get_assignment_group_grade_override_for_grading',
      {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          p_assignment_submission_id:
            submissionId,
          p_enrolment_id:
            enrolmentId
        })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Loading individual group grade failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  return Array.isArray(rows)
    ? rows[0] || null
    : null;
}


async function saveAssignmentGroupGradeOverride({
  submissionId,
  enrolmentId,
  instructorId,
  score
}) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/save_assignment_group_grade_override_for_grading',
      {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          p_assignment_submission_id:
            submissionId,
          p_enrolment_id:
            enrolmentId,
          p_instructor_id:
            instructorId,
          p_score:
            score
        })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Saving individual group grade failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  return Array.isArray(rows)
    ? rows[0] || null
    : null;
}


async function deleteAssignmentGroupGradeOverride(
  submissionId,
  enrolmentId,
  instructorId
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/delete_assignment_group_grade_override_for_grading',
      {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          p_assignment_submission_id:
            submissionId,
          p_enrolment_id:
            enrolmentId,
          p_instructor_id:
            instructorId
        })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Removing individual group grade failed (' +
        response.status +
        '): ' +
        detail
    );
  }
}


async function clearUnpublishedGrade(
  submissionId,
  instructorId
) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/rpc/clear_unpublished_grade',
      {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body: JSON.stringify({
          p_submission_id:
            submissionId,
          p_instructor_id:
            instructorId
        })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Clearing unpublished grade failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  return response.json();
}

async function updateSelfEvalResponse(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_responses?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to save self-evaluation (${response.status})`
    );
  }

  return response.json();
}

async function createPdExtensionRow(
  enrolmentId,
  weekId,
  extensionDays
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        enrolment_id: enrolmentId,
        week_id: weekId,
        text: '',
        submitted: false,
        submitted_at: null,
        extension_days: extensionDays
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to create PD extension record (${response.status}): ${detail}`
    );
  }

  return response.json();
}

async function createAssignmentExtensionRow(
  enrolmentId,
  courseAssignmentId,
  extensionDays
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        enrolment_id: enrolmentId,
        course_assignment_id: courseAssignmentId,
        text: '',
        submitted: false,
        submitted_at: null,
        extension_days: extensionDays
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to create assignment extension record (${response.status}): ${detail}`
    );
  }

  return response.json();
}

async function saveAssignmentExtensionRows(
  enrolmentId,
  courseAssignmentId,
  extensionDays
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=eq.${encodeURIComponent(enrolmentId)}&course_assignment_id=eq.${encodeURIComponent(courseAssignmentId)}&course_assignment_schedule_item_id=is.null&structured_form_instance_id=is.null`,
    {
      method: 'PATCH',

      headers: {
        'apikey': SUPABASE_KEY,

        'Authorization':
          'Bearer ' +
          SUPABASE_KEY,

        'Content-Type':
          'application/json',

        'Prefer':
          'return=representation'
      },

      body: JSON.stringify({
        extension_days:
          extensionDays
      })
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Failed to save assignment extension (${response.status}): ${detail}`
    );
  }

  const rows =
    await response.json();

  if (
    Array.isArray(rows) &&
    rows.length > 0
  ) {
    return rows;
  }

  return createAssignmentExtensionRow(
    enrolmentId,
    courseAssignmentId,
    extensionDays
  );
}


// STRUCTURED_FORM_INSTANCE_EXTENSIONS_V1

async function saveStructuredFormInstanceExtensionRows(
  enrolmentId,
  courseAssignmentId,
  instanceId,
  extensionDays,
  currentExtensions
) {
  const nextExtensions =
    currentExtensions &&
    typeof currentExtensions ===
      'object' &&
    !Array.isArray(
      currentExtensions
    )
      ? {
          ...currentExtensions
        }
      : {};

  if (extensionDays > 0) {
    nextExtensions[
      String(instanceId)
    ] = extensionDays;
  } else {
    delete nextExtensions[
      String(instanceId)
    ];
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=eq.${encodeURIComponent(enrolmentId)}&course_assignment_id=eq.${encodeURIComponent(courseAssignmentId)}&course_assignment_schedule_item_id=is.null&structured_form_instance_id=is.null`,
      {
        method: 'PATCH',

        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json',

          'Prefer':
            'return=representation'
        },

        body:
          JSON.stringify({
            structured_form_instance_extensions:
              nextExtensions
          })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Failed to save Structured Form instance extension (${response.status}): ${detail}`
    );
  }

  const rows =
    await response.json();

  if (
    Array.isArray(rows) &&
    rows.length > 0
  ) {
    return rows;
  }

  const createResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/assignment_submissions`,
      {
        method: 'POST',

        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json',

          'Prefer':
            'return=representation'
        },

        body:
          JSON.stringify({
            enrolment_id:
              enrolmentId,

            course_assignment_id:
              courseAssignmentId,

            text:
              '',

            submitted:
              false,

            submitted_at:
              null,

            extension_days:
              0,

            structured_form_instance_extensions:
              nextExtensions
          })
      }
    );

  if (!createResponse.ok) {
    const detail =
      await createResponse.text();

    throw new Error(
      `Failed to create Structured Form extension record (${createResponse.status}): ${detail}`
    );
  }

  return createResponse.json();
}


async function updateSubmissionExtension(
  table,
  id,
  extensionDays
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/${table}?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        extension_days: extensionDays
      })
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to save extension (${response.status})`
    );
  }

  return response.json();
}

async function unsubmitPd(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/pd_submissions?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        submitted: false,
        submitted_at: null
      })
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to unsubmit PD (${response.status})`
    );
  }

  return response.json();
}

async function unsubmitAssignment(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        submitted: false,
        submitted_at: null
      })
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to unsubmit assignment (${response.status})`
    );
  }

  const updatedSubmission = await response.json();

  // Keep instructor feedback, but withdraw it from the student.
  const feedbackResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_feedback?assignment_submission_id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        submitted: false,
        submitted_at: null,
        student_response: null
      })
    }
  );

  if (!feedbackResponse.ok) {
    const detail = await feedbackResponse.text();

    throw new Error(
      `Assignment was returned, but feedback could not be withdrawn (${feedbackResponse.status}): ${detail}`
    );
  }

  const updatedFeedback =
    await feedbackResponse.json();

  return {
    submission: updatedSubmission,
    feedback: updatedFeedback
  };
}

async function unsubmitSelfEval(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_responses?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        status: 'Draft',
        submitted_at: null
      })
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to unsubmit self-evaluation (${response.status})`
    );
  }

  return response.json();
}

async function cqHasQuestionImprovementAssignment(
  cqSubmissionId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_question_assignments?cq_submission_id=eq.${cqSubmissionId}&select=id&limit=1`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to check Question Improvement assignment (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();
  return rows.length > 0;
}

async function unsubmitCq(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to unsubmit Critical Question (${response.status}): ${detail}`
    );
  }
}

function getCqWindowStatus(week) {
  const today =
    new Date().toISOString().split('T')[0];

  if (!week?.cq_enabled) return 'disabled';
  if (!week?.class_date) return 'closed';

  const classDate =
    String(week.class_date).slice(0, 10);

  const parsed =
    new Date(`${classDate}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return 'closed';
  }

  const windowOpen = new Date(parsed);
  windowOpen.setDate(
    windowOpen.getDate() - 6
  );

  const windowOpenStr =
    windowOpen.toISOString().split('T')[0];

  if (
    today >= windowOpenStr &&
    today < classDate
  ) {
    return 'open';
  }

  if (today >= classDate) {
    return 'closed';
  }

  return 'not_open';
}

function getCqQuestionText(submission) {
  if (!submission) return '';

  const preferredKeys = [
    'question_text',
    'question',
    'text',
    'content',
    'response',
    'submission_text'
  ];

  for (const key of preferredKeys) {
    const value = submission[key];

    if (
      typeof value === 'string' &&
      value.trim()
    ) {
      return value.trim();
    }
  }

  const ignoredKeys = new Set([
    'id',
    'enrolment_id',
    'week_id',
    'question_type',
    'created_at',
    'updated_at',
    'submitted_at'
  ]);

  const fallback =
    Object.entries(submission).find(
      ([key, value]) =>
        !ignoredKeys.has(key) &&
        typeof value === 'string' &&
        value.trim() &&
        !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(
          value.trim()
        )
    );

  return fallback
    ? fallback[1].trim()
    : '';
}

function getStudentName(student) {
  return (
    student?.english_name ||
    student?.chinese_name ||
    student?.student_code ||
    '—'
  );
}

function normalizeFeedback(feedback) {
  if (Array.isArray(feedback)) {
    return feedback[0] || null;
  }

  return feedback || null;
}


function instructorFeedbackTime(
  feedback
) {
  return new Date(
    feedback?.submitted_at ||
    feedback?.updated_at ||
    feedback?.created_at ||
    0
  ).getTime();
}


function latestInstructorFeedback(
  rows,
  instructorId
) {
  if (
    !instructorId ||
    !Array.isArray(rows)
  ) {
    return null;
  }

  return (
    rows
      .filter(
        row =>
          String(
            row.instructor_id ||
            ''
          ) ===
          String(
            instructorId
          )
      )
      .slice()
      .sort(
        (a, b) =>
          instructorFeedbackTime(b) -
          instructorFeedbackTime(a)
      )[0] ||
    null
  );
}


function firstSubmittedWithScore(
  rows
) {
  return (
    (Array.isArray(rows)
      ? rows
      : []
    )
      .filter(
        row =>
          row.submitted ===
            true &&
          row.score !== null &&
          row.score !==
            undefined &&
          row.score !== ''
      )
      .slice()
      .sort(
        (a, b) =>
          instructorFeedbackTime(a) -
          instructorFeedbackTime(b)
      )[0] ||
    null
  );
}


function firstSubmittedWithStatus(
  rows
) {
  return (
    (Array.isArray(rows)
      ? rows
      : []
    )
      .filter(
        row =>
          row.submitted ===
            true &&
          String(
            row.completion_status ||
            ''
          ).trim()
      )
      .slice()
      .sort(
        (a, b) =>
          instructorFeedbackTime(a) -
          instructorFeedbackTime(b)
      )[0] ||
    null
  );
}


function instructorFeedbackName(
  feedback
) {
  return (
    feedback
      ?.instructors
      ?.name ||
    feedback
      ?.instructors
      ?.email ||
    'Instructor'
  );
}

function parseLocalDate(dateStr) {
  if (!dateStr) return null;

  const match = String(dateStr)
    .slice(0, 10)
    .match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

  if (!match) return null;

  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0,
    0,
    0,
    0
  );
}

function addDays(date, days) {
  if (!date) return null;

  const result = new Date(date);

  result.setDate(
    result.getDate() +
    Math.max(0, Number(days) || 0)
  );

  return result;
}

function getPdOriginalDeadline(
  week,
  course
) {
  if (
    !week?.class_date ||
    !course?.pd_due_day
  ) {
    return null;
  }

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const targetDay =
    dayNames.indexOf(course.pd_due_day);

  if (targetDay < 0) {
    return null;
  }

  const dueDate =
    parseLocalDate(week.class_date);

  if (!dueDate) {
    return null;
  }

  while (
    dueDate.getDay() !== targetDay
  ) {
    dueDate.setDate(
      dueDate.getDate() + 1
    );
  }

  dueDate.setHours(
    23,
    59,
    59,
    999
  );

  return dueDate;
}

function getAssignmentOriginalDeadline(
  assignment
) {
  const dueDate =
    parseLocalDate(
      assignment?.due_date
    );

  if (!dueDate) {
    return null;
  }

  dueDate.setHours(
    23,
    59,
    59,
    999
  );

  return dueDate;
}

function normaliseIdentityText(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    .test(
      String(value || '').trim()
    );
}

function collectExistingInstructorIds(
  pdSubmissions,
  assignmentSubmissions
) {
  const ids = new Set();

  const collect = feedback => {
    const rows =
      Array.isArray(feedback)
        ? feedback
        : feedback
          ? [feedback]
          : [];

    rows.forEach(row => {
      if (
        looksLikeUuid(
          row?.instructor_id
        )
      ) {
        ids.add(
          row.instructor_id
        );
      }
    });
  };

  (pdSubmissions || []).forEach(
    row =>
      collect(row.pd_feedback)
  );

  (
    assignmentSubmissions || []
  ).forEach(
    row =>
      collect(
        row.assignment_feedback
      )
  );

  return [...ids];
}

async function resolveInstructorId({
  session,
  course,
  pdSubmissions,
  assignmentSubmissions
}) {
  const explicitCandidates = [
    session?.instructorId,
    session?.instructor_id,
    session?.instructor?.id,
    course?.instructor_id,
    course?.instructorId,
    course?.instructor?.id,
    course?.instructors?.id
  ];

  const explicit =
    explicitCandidates.find(
      looksLikeUuid
    );

  if (explicit) {
    return explicit;
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?select=*`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization':
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  let instructors = [];

  if (response.ok) {
    instructors =
      await response.json();

    const sessionEmail =
      normaliseIdentityText(
        session?.email
      );

    const sessionNames = [
      session?.englishName,
      session?.chineseName
    ]
      .map(
        normaliseIdentityText
      )
      .filter(Boolean);

    if (sessionEmail) {
      const emailMatches =
        instructors.filter(
          instructor => {
            const emails = [
              instructor?.email,
              instructor?.instructor_email,
              instructor?.contact_email
            ]
              .map(
                normaliseIdentityText
              )
              .filter(Boolean);

            return emails.includes(
              sessionEmail
            );
          }
        );

      if (
        emailMatches.length === 1 &&
        looksLikeUuid(
          emailMatches[0].id
        )
      ) {
        return emailMatches[0].id;
      }
    }

    if (
      sessionNames.length > 0
    ) {
      const nameMatches =
        instructors.filter(
          instructor => {
            const names = [
              instructor?.name,
              instructor?.english_name,
              instructor?.chinese_name,
              instructor?.display_name,
              instructor?.instructor_name
            ]
              .map(
                normaliseIdentityText
              )
              .filter(Boolean);

            return sessionNames.some(
              name =>
                names.includes(name)
            );
          }
        );

      if (
        nameMatches.length === 1 &&
        looksLikeUuid(
          nameMatches[0].id
        )
      ) {
        return nameMatches[0].id;
      }
    }
  }

  const existingIds =
    collectExistingInstructorIds(
      pdSubmissions,
      assignmentSubmissions
    );

  if (existingIds.length === 1) {
    return existingIds[0];
  }

  if (
    instructors.length === 1 &&
    looksLikeUuid(
      instructors[0].id
    )
  ) {
    return instructors[0].id;
  }

  throw new Error(
    'Could not determine the instructor account for this feedback. ' +
    'The logged-in master account must match an instructor by email or name.'
  );
}

function formatSubmissionReceivedAt(
  value
) {
  if (!value) {
    return '';
  }

  const date =
    value instanceof Date
      ? value
      : new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '';
  }

  return new Intl.DateTimeFormat(
    'en-GB',
    {
      timeZone:
        'Asia/Taipei',
      day:
        'numeric',
      month:
        'short',
      year:
        'numeric',
      hour:
        '2-digit',
      minute:
        '2-digit',
      second:
        '2-digit',
      hourCycle:
        'h23'
    }
  ).format(date);
}


function SubmissionHeading({
  label = 'Student Submission',
  submittedAt
}) {
  const receivedAt =
    formatSubmissionReceivedAt(
      submittedAt
    );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: '7px',
        marginBottom: '8px'
      }}
    >
      <div
        style={
          styles.submissionLabel
        }
      >
        {label}
      </div>

      {receivedAt && (
        <div
          style={{
            color: '#627d98',
            fontSize: '11px',
            fontWeight: 500
          }}
        >
          · Received {receivedAt}
        </div>
      )}
    </div>
  );
}


function formatDeadline(date) {
  if (!date) {
    return 'Not configured';
  }

  return date.toLocaleString(
    'en-GB',
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }
  );
}

function formatDateOnly(value) {
  if (!value) return 'Not configured';

  const date = new Date(
    `${String(value).slice(0, 10)}T12:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString(
    'en-GB',
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }
  );
}

function AiDisclosureReadOnly({ submission }) {
  if (typeof submission?.ai_used !== 'boolean') {
    return null;
  }

  return (
    <div style={styles.aiDisclosureBox}>
      <div style={styles.aiDisclosureTitle}>
        AI Disclosure
      </div>

      <div style={styles.aiDisclosureRow}>
        Used AI:{' '}
        <strong>
          {submission.ai_used ? 'Yes' : 'No'}
        </strong>
      </div>

      {submission.ai_used && (
        <>
          <div style={styles.aiDisclosureRow}>
            AI policy confirmed:{' '}
            <strong>
              {submission.ai_policy_confirmed ? 'Yes' : 'No'}
            </strong>
          </div>

          <div style={styles.aiDisclosureRow}>
            Conversation sharing URL:{' '}
            {submission.ai_conversation_url ? (
              <a
                href={submission.ai_conversation_url}
                target="_blank"
                rel="noreferrer"
                style={styles.aiDisclosureLink}
              >
                {submission.ai_conversation_url}
              </a>
            ) : (
              <span>Not provided</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// STRUCTURED_NOT_APPLICABLE_CHOICE_V1

function StructuredSnapshotValue({ field }) {
  const OTHER_PREFIX =
    '__HSS_OTHER_TEXT__:';

  const NOT_APPLICABLE_PREFIX =
    '__HSS_NOT_APPLICABLE_TEXT__:';


  if (
    field.field_type ===
    'multiple_choice'
  ) {
    const raw =
      Array.isArray(
        field.response_json
      )
        ? field.response_json.map(
            item =>
              String(item)
          )
        : [];

    const otherDetailRow =
      raw.find(
        item =>
          item.startsWith(
            OTHER_PREFIX
          )
      );

    const notApplicableDetailRow =
      raw.find(
        item =>
          item.startsWith(
            NOT_APPLICABLE_PREFIX
          )
      );

    const otherDetail =
      otherDetailRow
        ? otherDetailRow.slice(
            OTHER_PREFIX.length
          )
        : '';

    const notApplicableDetail =
      notApplicableDetailRow
        ? notApplicableDetailRow.slice(
            NOT_APPLICABLE_PREFIX
              .length
          )
        : '';

    const values =
      raw.filter(
        item =>
          !item.startsWith(
            OTHER_PREFIX
          ) &&
          !item.startsWith(
            NOT_APPLICABLE_PREFIX
          )
      );

    if (!values.length) {
      return '(No response)';
    }

    return values
      .map(
        value => {
          const normalised =
            String(value)
              .trim()
              .toLowerCase();

          if (
            normalised ===
              'other' &&
            otherDetail
          ) {
            return (
              'Other — ' +
              otherDetail
            );
          }

          if (
            normalised ===
              'not applicable' &&
            notApplicableDetail
          ) {
            return (
              'Not Applicable — ' +
              notApplicableDetail
            );
          }

          return value;
        }
      )
      .join(', ');
  }


  if (
    field.field_type ===
      'single_choice'
  ) {
    const value =
      String(
        field.response_text ||
        ''
      );

    if (!value) {
      return '(No response)';
    }

    if (
      value.startsWith(
        OTHER_PREFIX
      )
    ) {
      const detail =
        value.slice(
          OTHER_PREFIX.length
        );

      return detail
        ? 'Other — ' +
            detail
        : 'Other';
    }

    if (
      value.startsWith(
        NOT_APPLICABLE_PREFIX
      )
    ) {
      const detail =
        value.slice(
          NOT_APPLICABLE_PREFIX
            .length
        );

      return detail
        ? 'Not Applicable — ' +
            detail
        : 'Not Applicable';
    }

    return value;
  }


  if (
    field.field_type ===
      'short_text'
  ) {
    return (
      field.response_text ||
      '(No response)'
    );
  }


  return field.response_text ? (
    <div
      style={
        styles.richTextDisplay
      }
      dangerouslySetInnerHTML={{
        __html:
          field.response_text
      }}
    />
  ) : '(No response)';
}


function StructuredSnapshotView({ snapshot }) {
  const sections = Array.isArray(snapshot?.sections)
    ? snapshot.sections
    : [];

  if (!sections.length) {
    return <div style={styles.muted}>(The saved snapshot has no form content.)</div>;
  }

  return (
    <div style={styles.structuredSnapshot}>
      {sections.map((section, sectionIndex) => (
        <section
          key={section.id || sectionIndex}
          style={{
            ...styles.structuredSection,
            ...(section.active ? {} : styles.structuredSectionInactive)
          }}
        >
          <div style={styles.structuredSectionHeader}>
            <div>
              <strong>Section {sectionIndex + 1}: {section.title}</strong>
              {section.description && (
                <div style={styles.structuredDescription}>{section.description}</div>
              )}
            </div>
            <span style={section.active ? styles.structuredActive : styles.structuredInactive}>
              {section.active ? 'Open in this instance' : 'Historical context'}
            </span>
          </div>

          {(section.fields || []).map((field, fieldIndex) => (
            <div key={field.id || fieldIndex} style={styles.structuredField}>
              <div style={styles.structuredPrompt}>
                {fieldIndex + 1}. {field.label}
                {field.required ? ' · Required' : ''}
              </div>
              {field.prompt && <div style={styles.structuredDescription}>{field.prompt}</div>}
              <div style={styles.structuredAnswer}>
                <StructuredSnapshotValue field={field} />
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

export default function GradingPanel({
  target,
  course,
  session,
  enrolments,
  weeks,
  cqSubmissions,
  pdSubmissions,
  assignmentSubmissions,
  selfEvalResponses,
  courseAssignments,
  onClose,
  onUpdate,
  onSubmitPrevious = null,
  onSubmitNext = null
}) {
  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const [success, setSuccess] =
    useState('');

  const [
    selfEvalQuestions,
    setSelfEvalQuestions
  ] = useState([]);

  const [
    courseSpecificQuestion,
    setCourseSpecificQuestion
  ] = useState(null);

  const [comment, setComment] =
    useState('');

  const [
    instructorNote,
    setInstructorNote
  ] = useState('');

  const [
    completionStatus,
    setCompletionStatus
  ] = useState('');

  const [finalGrade, setFinalGrade] =
    useState('');

  const [
    selfEvalComments,
    setSelfEvalComments
  ] = useState({});

  const [
    extensionDays,
    setExtensionDays
  ] = useState('0');
  const [score, setScore] = useState('');

  const [
    groupGradeOverride,
    setGroupGradeOverride
  ] = useState(null);

  const [
    groupGradeOverrideOpen,
    setGroupGradeOverrideOpen
  ] = useState(false);

  const [
    groupGradeOverrideScore,
    setGroupGradeOverrideScore
  ] = useState('');

  const [
    groupGradeOverrideSaving,
    setGroupGradeOverrideSaving
  ] = useState(false);

  const [selectedStructuredSnapshotId, setSelectedStructuredSnapshotId] =
    useState('');

  const overlayRef =
    useRef(null);

  useEffect(() => {
    if (
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      'hidden';

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, []);

  const [
    currentInstructorId,
    setCurrentInstructorId
  ] = useState(null);

  const [
    instructorFeedbackRows,
    setInstructorFeedbackRows
  ] = useState([]);

  /*
   * Upload-on-behalf must work even if the student has never
   * successfully created a submission. Resolve the instructor
   * independently of the feedback-loading path.
   */
  useEffect(() => {
    let cancelled =
      false;

    resolveInstructorId({
      session,
      course,
      pdSubmissions,
      assignmentSubmissions
    })
      .then(
        id => {
          if (
            !cancelled &&
            id
          ) {
            setCurrentInstructorId(
              id
            );
          }
        }
      )
      .catch(
        err =>
          console.error(
            'Resolving instructor for upload-on-behalf failed:',
            err
          )
      );

    return () => {
      cancelled =
        true;
    };
  }, [
    session,
    course,
    pdSubmissions,
    assignmentSubmissions
  ]);

  useEffect(() => {
    if (
      overlayRef.current
    ) {
      overlayRef.current.scrollTop =
        0;
    }
  }, [
    target.type,
    target.enrolmentId,
    target.weekId,
    target.assignmentId,
    target.scheduleItemId,
    target.structuredFormInstanceId
  ]);


  const enrolment =
    enrolments.find(
      e =>
        e.id ===
        target.enrolmentId
    );

  const student =
    enrolment?.students;

  const week =
    weeks.find(
      w =>
        w.id === target.weekId
    );

  const cqSub =
    target.type === 'cq'
      ? (cqSubmissions || []).find(
          submission =>
            submission.enrolment_id ===
              target.enrolmentId &&
            submission.week_id ===
              target.weekId
        )
      : null;

  const pdSub =
    target.type === 'pd'
      ? pdSubmissions.find(
          p =>
            p.enrolment_id ===
              target.enrolmentId &&
            p.week_id ===
              target.weekId
        )
      : null;

  const assignmentSub =
    target.type === 'assignment'
      ? assignmentSubmissions.find(
          s =>
            s.enrolment_id ===
              target.enrolmentId &&
            s.course_assignment_id ===
              target.assignmentId &&
            (
              target.structuredFormInstanceId
                ? selectedStructuredSnapshotId
                  ? String(s.id) === String(selectedStructuredSnapshotId)
                  : String(s.structured_form_instance_id) ===
                      String(target.structuredFormInstanceId)
                : target.scheduleItemId
                ? String(s.course_assignment_schedule_item_id) ===
                    String(target.scheduleItemId)
                : !s.course_assignment_schedule_item_id &&
                  !s.structured_form_instance_id
            )
        )
      : null;

  const assignmentExtensionRows =
    target.type === 'assignment'
      ? assignmentSubmissions.filter(
          s =>
            s.enrolment_id ===
              target.enrolmentId &&
            s.course_assignment_id ===
              target.assignmentId &&
            !s.course_assignment_schedule_item_id &&
            !s.structured_form_instance_id
        )
      : [];

  const assignmentExtensionSub =
    assignmentExtensionRows.reduce(
      (best, row) => {
        if (!best) {
          return row;
        }

        return (
          Math.max(
            0,
            Number(
              row.extension_days
            ) || 0
          ) >
          Math.max(
            0,
            Number(
              best.extension_days
            ) || 0
          )
        )
          ? row
          : best;
      },
      null
    );

  const assignmentWideExtensionDays =
    Math.max(
      0,
      Number(
        assignmentExtensionSub
          ?.extension_days
      ) || 0
    );

  const structuredFormInstanceExtensions =
    assignmentExtensionSub
      ?.structured_form_instance_extensions &&
    typeof assignmentExtensionSub
      .structured_form_instance_extensions ===
      'object' &&
    !Array.isArray(
      assignmentExtensionSub
        .structured_form_instance_extensions
    )
      ? assignmentExtensionSub
          .structured_form_instance_extensions
      : {};

  const structuredInstanceExtensionDays =
    target.type ===
      'assignment' &&
    target.structuredFormInstanceId
      ? Math.max(
          0,
          Number(
            structuredFormInstanceExtensions[
              String(
                target
                  .structuredFormInstanceId
              )
            ]
          ) || 0
        )
      : 0;


  const selfEvalResponse =
    target.type === 'selfeval'
      ? selfEvalResponses.find(
          r =>
            r.enrolment_id ===
              target.enrolmentId &&
            r.section ===
              target.section
        )
      : null;

  const pdFeedback =
    normalizeFeedback(
      pdSub?.pd_feedback
    );

  const assignmentFeedback =
    normalizeFeedback(
      assignmentSub
        ?.assignment_feedback
    );


  const activeSubmissionType =
    target.type === 'pd'
      ? 'pd'
      : target.type ===
          'assignment'
        ? 'assignment'
        : null;


  const activeSubmissionId =
    target.type === 'pd'
      ? pdSub?.id
      : target.type ===
          'assignment'
        ? assignmentSub?.id
        : null;


  const myInstructorFeedback =
    latestInstructorFeedback(
      instructorFeedbackRows,
      currentInstructorId
    );


  const otherInstructorFeedback =
    instructorFeedbackRows
      .filter(
        row =>
          row.submitted ===
            true &&
          String(
            row.instructor_id ||
            ''
          ) !==
          String(
            currentInstructorId ||
            ''
          )
      )
      .slice()
      .sort(
        (a, b) =>
          instructorFeedbackTime(a) -
          instructorFeedbackTime(b)
      );


  const hasUnpublishedGrade =
    target.type === 'assignment' &&
    instructorFeedbackRows.some(
      row =>
        row.submitted === true &&
        row.is_unpublished === true
    );

  const hasPublishedGrade =
    target.type === 'assignment' &&
    assignmentFeedback?.submitted ===
      true;


  const instructorGradingMethod =
    course
      ?.instructor_grading_method ===
    'average'
      ? 'average'
      : 'single';


  const firstScoreFeedback =
    firstSubmittedWithScore(
      instructorFeedbackRows
    );


  const firstStatusFeedback =
    firstSubmittedWithStatus(
      instructorFeedbackRows
    );


  const scoreLocked =
    course?.grading_mode ===
      'traditional' &&
    instructorGradingMethod ===
      'single' &&
    !!firstScoreFeedback &&
    String(
      firstScoreFeedback
        .instructor_id ||
      ''
    ) !==
    String(
      currentInstructorId ||
      ''
    );


  const completionLocked =
    course?.grading_mode !==
      'traditional' &&
    !!firstStatusFeedback &&
    String(
      firstStatusFeedback
        .instructor_id ||
      ''
    ) !==
    String(
      currentInstructorId ||
      ''
    );


  const effectiveFeedback =
    target.type === 'pd'
      ? pdFeedback
      : target.type ===
          'assignment'
        ? assignmentFeedback
        : null;


  const submittedScoreCount =
    instructorFeedbackRows.filter(
      row =>
        row.submitted ===
          true &&
        row.score !== null &&
        row.score !==
          undefined &&
        row.score !== ''
    ).length;


  const effectiveOutcomeText =
    course?.grading_mode ===
      'traditional'
      ? (
          effectiveFeedback?.score !==
            null &&
          effectiveFeedback?.score !==
            undefined
            ? (
                instructorGradingMethod ===
                  'average'
                  ? 'Effective grade: ' +
                    Number(
                      effectiveFeedback
                        .score
                    ) +
                    '% — average of ' +
                    submittedScoreCount +
                    ' instructor grade' +
                    (
                      submittedScoreCount ===
                      1
                        ? ''
                        : 's'
                    )
                  : 'Official grade: ' +
                    Number(
                      effectiveFeedback
                        .score
                    ) +
                    (
                      firstScoreFeedback
                        ? '% — set by ' +
                          instructorFeedbackName(
                            firstScoreFeedback
                          )
                        : '%'
                    )
              )
            : ''
        )
      : (
          effectiveFeedback
            ?.completion_status
            ? 'Official status: ' +
              effectiveFeedback
                .completion_status +
              (
                firstStatusFeedback
                  ? ' — set by ' +
                    instructorFeedbackName(
                      firstStatusFeedback
                    )
                  : ''
              )
            : ''
        );


  const courseAssignment =
    target.type === 'assignment'
      ? courseAssignments?.find(
          a =>
            a.id ===
            target.assignmentId
        )
      : null;


  const isGroupAssignment =
    target.type ===
      'assignment' &&
    courseAssignment
      ?.groups_enabled ===
      true &&
    Number(
      courseAssignment
        ?.group_count
    ) > 0 &&
    (
      courseAssignment?.assignment_types?.group_submission === true ||
      courseAssignment?.assignment_types?.assignment_kind === 'structured_form'
    );


  const gradeOverrideEnrolmentId =
    target
      .gradeOverrideEnrolmentId ||
    target.enrolmentId;


  const gradeOverrideEnrolment =
    enrolments.find(
      row =>
        String(row.id) ===
        String(
          gradeOverrideEnrolmentId
        )
    ) || null;


  const gradeOverrideStudentName =
    getStudentName(
      gradeOverrideEnrolment
        ?.students
    );


  useEffect(() => {
    let cancelled = false;

    async function loadGroupGradeOverride() {
      if (
        !isGroupAssignment ||
        course?.grading_mode !==
          'traditional' ||
        !activeSubmissionId ||
        !gradeOverrideEnrolmentId
      ) {
        setGroupGradeOverride(null);
        setGroupGradeOverrideScore('');
        setGroupGradeOverrideOpen(false);
        return;
      }

      try {
        const row =
          await fetchAssignmentGroupGradeOverride(
            activeSubmissionId,
            gradeOverrideEnrolmentId
          );

        if (cancelled) {
          return;
        }

        setGroupGradeOverride(
          row
        );

        setGroupGradeOverrideScore(
          row?.score !==
            null &&
          row?.score !==
            undefined
            ? String(
                row.score
              )
            : ''
        );

        setGroupGradeOverrideOpen(
          false
        );

      } catch (err) {
        console.error(
          'Loading individual group-grade override failed:',
          err
        );

        if (!cancelled) {
          setGroupGradeOverride(null);
          setGroupGradeOverrideScore('');
          setGroupGradeOverrideOpen(false);
        }
      }
    }

    loadGroupGradeOverride();

    return () => {
      cancelled = true;
    };
  }, [
    isGroupAssignment,
    course?.grading_mode,
    activeSubmissionId,
    gradeOverrideEnrolmentId
  ]);


  useEffect(() => {
    let cancelled =
      false;

    async function loadInstructorFeedback() {
      if (
        !activeSubmissionType ||
        !activeSubmissionId
      ) {
        setInstructorFeedbackRows(
          []
        );

        return;
      }

      try {
        const instructorId =
          await resolveInstructorId({
            session,
            course,
            pdSubmissions,
            assignmentSubmissions
          });

        if (cancelled) {
          return;
        }

        setCurrentInstructorId(
          instructorId
        );

        const rows =
          await fetchInstructorSubmissionFeedback(
            activeSubmissionType,
            activeSubmissionId
          );

        if (!cancelled) {
          setInstructorFeedbackRows(
            rows
          );
        }

      } catch (err) {
        console.error(
          'Loading multi-instructor feedback failed:',
          err
        );

        if (!cancelled) {
          setInstructorFeedbackRows(
            []
          );

          setError(
            err?.message ||
            'Could not load instructor feedback.'
          );
        }
      }
    }

    loadInstructorFeedback();

    return () => {
      cancelled = true;
    };
  }, [
    activeSubmissionType,
    activeSubmissionId,
    course,
    session,
    pdSubmissions,
    assignmentSubmissions
  ]);


  const structuredSnapshots =
    target.type === 'assignment' && target.structuredFormInstanceId
      ? assignmentSubmissions
          .filter(
            submission =>
              submission.enrolment_id === target.enrolmentId &&
              submission.course_assignment_id === target.assignmentId &&
              submission.structured_form_instance_id &&
              submission.submitted
          )
          .sort(
            (a, b) =>
              Number(a.structured_form_snapshot?.instance?.instance_order || 0) -
              Number(b.structured_form_snapshot?.instance?.instance_order || 0)
          )
      : [];

  useEffect(() => {
    if (!target.structuredFormInstanceId) {
      setSelectedStructuredSnapshotId('');
      return;
    }

    const targetSnapshot = assignmentSubmissions.find(
      submission =>
        submission.enrolment_id === target.enrolmentId &&
        submission.course_assignment_id === target.assignmentId &&
        String(submission.structured_form_instance_id) ===
          String(target.structuredFormInstanceId)
    );

    setSelectedStructuredSnapshotId(targetSnapshot?.id || '');
  }, [
    target.enrolmentId,
    target.assignmentId,
    target.structuredFormInstanceId,
    assignmentSubmissions
  ]);

  const savedExtensionDays =
    target.type === 'pd'
      ? Math.max(
          0,
          Number(
            pdSub?.extension_days
          ) || 0
        )
      : target.type ===
          'assignment'
        ? (
            target
              .structuredFormInstanceId
              ? structuredInstanceExtensionDays
              : assignmentWideExtensionDays
          )
        : 0;

  const effectiveSavedExtensionDays =
    target.type ===
      'assignment' &&
    target.structuredFormInstanceId
      ? Math.max(
          assignmentWideExtensionDays,
          structuredInstanceExtensionDays
        )
      : savedExtensionDays;


  const originalDeadline =
    target.type === 'pd'
      ? getPdOriginalDeadline(
          week,
          course
        )
      : target.type ===
          'assignment'
        ? target.structuredFormInstanceId
          ? getAssignmentOriginalDeadline({
              due_date: target.structuredFormCloseDate
            })
          : getAssignmentOriginalDeadline(
            target.scheduleItemId
              ? { due_date: target.scheduleDueDate }
              : courseAssignment
          )
        : null;

  const effectiveDeadline =
    originalDeadline
      ? addDays(
          originalDeadline,
          effectiveSavedExtensionDays
        )
      : null;

  const deadlinePassed =
    effectiveDeadline
      ? new Date() >
        effectiveDeadline
      : true;

  useEffect(() => {
    if (
      target.type === 'pd' ||
      target.type ===
        'assignment'
    ) {
      setComment(
        myInstructorFeedback
          ?.comment ||
        ''
      );

      setInstructorNote(
        myInstructorFeedback
          ?.instructor_note ||
        ''
      );

      setCompletionStatus(
        myInstructorFeedback
          ?.completion_status ||
        ''
      );

      setScore(
        myInstructorFeedback
          ?.score ??
        ''
      );
    }

    if (
      target.type ===
      'selfeval'
    ) {
      setFinalGrade(
        selfEvalResponse
          ?.final_grade || ''
      );

      setSelfEvalComments(
        selfEvalResponse
          ?.instructor_comment || {}
      );
    }
  }, [
    target,
    myInstructorFeedback,
    selfEvalResponse
  ]);


  useEffect(() => {
    if (
      target.type === 'pd'
    ) {
      setExtensionDays(
        String(
          Math.max(
            0,
            Number(
              pdSub?.extension_days
            ) || 0
          )
        )
      );
    } else if (
      target.type ===
      'assignment'
    ) {
      setExtensionDays(
        String(
          target
            .structuredFormInstanceId
            ? structuredInstanceExtensionDays
            : assignmentWideExtensionDays
        )
      );
    }
  }, [
    target.type,
    target.enrolmentId,
    target.weekId,
    target.assignmentId,
    target.scheduleItemId,
    pdSub?.id,
    pdSub?.extension_days,
    assignmentExtensionSub?.id,
    assignmentExtensionSub?.extension_days,
    assignmentExtensionSub
      ?.structured_form_instance_extensions,
    target.structuredFormInstanceId,
    assignmentWideExtensionDays,
    structuredInstanceExtensionDays
  ]);

  useEffect(() => {
    if (
      target.type !==
      'selfeval'
    ) {
      return;
    }

    setLoading(true);

    Promise.all([
      fetchSelfEvalQuestions(),
      fetchCourseSelfEvalQuestion(
        course.id,
        target.section
      )
    ])
      .then(([q, cq]) => {
        setSelfEvalQuestions(
          Array.isArray(q)
            ? q
            : []
        );

        setCourseSpecificQuestion(
          cq
        );
      })
      .finally(() =>
        setLoading(false)
      );
  }, [
    target,
    course.id
  ]);

  async function handleSavePdFeedback(
    submit,
    navigateDirection = null
  ) {
    if (!pdSub?.id) {
      setError(
        'No PD submission found.'
      );
      return;
    }

    if (
      course?.grading_mode ===
        'traditional' &&
      !scoreLocked
    ) {
      if (
        submit &&
        (
          score === '' ||
          score === null ||
          score === undefined
        )
      ) {
        setError(
          'Enter a score from 0 to 100 before submitting feedback.'
        );
        return;
      }

      if (
        score !== '' &&
        score !== null &&
        score !== undefined
      ) {
        const numericScore =
          Number(score);

        if (
          !Number.isFinite(
            numericScore
          ) ||
          numericScore < 0 ||
          numericScore > 100
        ) {
          setError(
            'Score must be between 0 and 100.'
          );
          return;
        }
      }
    }

    if (
  course?.grading_mode !==
    'traditional' &&
  !completionLocked &&
  submit &&
  !String(
    completionStatus || ''
  ).trim()
) {
  setError(
    'Select a completion status before submitting feedback.'
  );

  return;
}

    setSaving(true);
    setError('');

    try {
      const instructorId =
        currentInstructorId ||
        await resolveInstructorId({
          session,
          course,
          pdSubmissions,
          assignmentSubmissions
        });

      await saveInstructorSubmissionFeedback(
        'pd',
        pdSub.id,
        instructorId,
        {
          comment,

          instructor_note:
            instructorNote,

          completion_status:
            completionLocked
              ? (
                  myInstructorFeedback
                    ?.completion_status ||
                  null
                )
              : (
                  completionStatus ||
                  null
                ),

          score:
            course?.grading_mode ===
              'traditional'
              ? (
                  scoreLocked
                    ? (
                        myInstructorFeedback
                          ?.score ??
                        null
                      )
                    : (
                        score === '' ||
                        score === null ||
                        score === undefined
                          ? null
                          : Number(
                              score
                            )
                      )
                )
              : null,

          submitted:
            submit,

          submitted_at:
            submit
              ? new Date()
                  .toISOString()
              : null
        }
      );

      const rows =
        await fetchInstructorSubmissionFeedback(
          'pd',
          pdSub.id
        );

      setInstructorFeedbackRows(
        rows
      );

      const refreshedSubmission =
        await fetchSubmissionWithEffectiveFeedback(
          'pd',
          pdSub.id
        );

      const nextPdSubmissions =
        pdSubmissions.map(
          submission =>
            String(
              submission.id
            ) ===
            String(
              pdSub.id
            )
              ? (
                  refreshedSubmission ||
                  submission
                )
              : submission
        );

      onUpdate(
        'pd',
        nextPdSubmissions
      );

      if (submit &&
          String(completionStatus || '').toLowerCase() === 'incomplete') {
        try {
          await notifyReleasedIncomplete({
            session,
            courseId: course.id,
            submissionId: pdSub.id,
            submissionType: 'pd'
          });
        } catch (error) {
          console.error('Diary saved; Incomplete push failed:', error);
        }
      }

      setSuccess(
        submit
          ? 'Feedback submitted.'
          : 'Draft saved.'
      );

      if (submit) {
        if (
          navigateDirection ===
            'previous' &&
          onSubmitPrevious
        ) {
          onSubmitPrevious();

        } else if (
          navigateDirection ===
            'next' &&
          onSubmitNext
        ) {
          onSubmitNext();

        } else {
          onClose();
        }
      }

    } catch (err) {
      console.error(
        'PD feedback save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save feedback.'
      );

    } finally {
      setSaving(false);
    }
  }


  async function handleSaveAssignmentFeedback(
    submit,
    navigateDirection = null
  ) {
    if (!assignmentSub?.id) {
      setError(
        target.assignmentGroupNumber
          ? 'This group has not submitted this assignment yet.'
          : 'The student has not submitted this assignment yet.'
      );

      return;
    }

    if (
      course?.grading_mode ===
        'traditional' &&
      !scoreLocked
    ) {
      if (
        submit &&
        (
          score === '' ||
          score === null ||
          score === undefined
        )
      ) {
        setError(
          'Enter a score from 0 to 100 before submitting feedback.'
        );

        return;
      }

      if (
        score !== '' &&
        score !== null &&
        score !== undefined
      ) {
        const numericScore =
          Number(score);

        if (
          !Number.isFinite(
            numericScore
          ) ||
          numericScore < 0 ||
          numericScore > 100
        ) {
          setError(
            'Score must be between 0 and 100.'
          );

          return;
        }
      }
    }

if (
  course?.grading_mode !==
    'traditional' &&
  !completionLocked &&
  submit &&
  !String(
    completionStatus || ''
  ).trim()
) {
  setError(
    'Select a completion status before submitting feedback.'
  );

  return;
}

    setSaving(true);
    setError('');

    try {
      const instructorId =
        currentInstructorId ||
        await resolveInstructorId({
          session,
          course,
          pdSubmissions,
          assignmentSubmissions
        });

      const feedbackPayload = {
        comment,

        instructor_note:
          instructorNote,

        completion_status:
          completionLocked
            ? (
                myInstructorFeedback
                  ?.completion_status ||
                null
              )
            : (
                completionStatus ||
                null
              ),

        score:
          course?.grading_mode ===
            'traditional'
            ? (
                scoreLocked
                  ? (
                      myInstructorFeedback
                        ?.score ??
                      null
                    )
                  : (
                      score === '' ||
                      score === null ||
                      score === undefined
                        ? null
                        : Number(
                            score
                          )
                    )
              )
            : null,

        submitted:
          submit,

        submitted_at:
          submit
            ? new Date()
                .toISOString()
            : null
      };

      /*
       * Save Draft is unchanged.
       *
       * Immediate Submit Feedback is unchanged.
       *
       * Only COMPLETED grading on a manual-publication assignment
       * is diverted into the new unpublished publication layer.
       */
      if (
        submit &&
        courseAssignment
          ?.grade_publication_mode ===
          'manual'
      ) {
        await saveUnpublishedInstructorSubmissionFeedback(
          assignmentSub.id,
          instructorId,
          feedbackPayload
        );
      } else {
        await saveInstructorSubmissionFeedback(
          'assignment',
          assignmentSub.id,
          instructorId,
          feedbackPayload
        );
      }

      const rows =
        await fetchInstructorSubmissionFeedback(
          'assignment',
          assignmentSub.id
        );

      setInstructorFeedbackRows(
        rows
      );

      const refreshedSubmission =
        await fetchSubmissionWithEffectiveFeedback(
          'assignment',
          assignmentSub.id
        );

      const nextAssignmentSubmissions =
        assignmentSubmissions.map(
          submission =>
            String(
              submission.id
            ) ===
            String(
              assignmentSub.id
            )
              ? (
                  refreshedSubmission ||
                  submission
                )
              : submission
        );

      onUpdate(
        'assignment',
        nextAssignmentSubmissions
      );

      if (
        submit &&
        courseAssignment?.grade_publication_mode !== 'manual' &&
        String(completionStatus || '').toLowerCase() === 'incomplete'
      ) {
        try {
          await notifyReleasedIncomplete({
            session,
            courseId: course.id,
            submissionId: assignmentSub.id,
            submissionType: 'assignment'
          });
        } catch (error) {
          console.error('Feedback saved; Incomplete push failed:', error);
        }
      }

      setSuccess(
        submit
          ? (
              courseAssignment
                ?.grade_publication_mode ===
                'manual'
                ? 'Graded, unpublished.'
                : 'Feedback submitted.'
            )
          : 'Draft saved.'
      );

      if (submit) {
        if (
          navigateDirection ===
            'previous' &&
          onSubmitPrevious
        ) {
          onSubmitPrevious();

        } else if (
          navigateDirection ===
            'next' &&
          onSubmitNext
        ) {
          onSubmitNext();

        } else {
          onClose();
        }
      }

    } catch (err) {
      console.error(
        'Assignment feedback save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save feedback.'
      );

    } finally {
      setSaving(false);
    }
  }


  async function handleSaveGroupGradeOverride() {
    if (
      !isGroupAssignment ||
      !activeSubmissionId ||
      !gradeOverrideEnrolmentId
    ) {
      return;
    }

    const parsed =
      Number(
        groupGradeOverrideScore
      );

    if (
      groupGradeOverrideScore ===
        '' ||
      !Number.isFinite(parsed) ||
      parsed < 0 ||
      parsed > 100
    ) {
      setError(
        'Individual grade must be between 0 and 100.'
      );
      return;
    }

    setGroupGradeOverrideSaving(
      true
    );

    setError('');
    setSuccess('');

    try {
      const row =
        await saveAssignmentGroupGradeOverride({
          submissionId:
            activeSubmissionId,

          enrolmentId:
            gradeOverrideEnrolmentId,

          instructorId:
            currentInstructorId,

          score:
            parsed
        });

      setGroupGradeOverride(
        row || {
          score:
            parsed
        }
      );

      setGroupGradeOverrideScore(
        String(parsed)
      );

      setGroupGradeOverrideOpen(
        false
      );

      setSuccess(
        'Individual grade override saved for ' +
          gradeOverrideStudentName +
          '.'
      );

    } catch (err) {
      console.error(
        'Individual group-grade override save failed:',
        err
      );

      setError(
        err?.message ||
        'Could not save the individual grade override.'
      );

    } finally {
      setGroupGradeOverrideSaving(
        false
      );
    }
  }


  async function handleRemoveGroupGradeOverride() {
    if (
      !activeSubmissionId ||
      !gradeOverrideEnrolmentId
    ) {
      return;
    }

    setGroupGradeOverrideSaving(
      true
    );

    setError('');
    setSuccess('');

    try {
      await deleteAssignmentGroupGradeOverride(
        activeSubmissionId,
        gradeOverrideEnrolmentId,
        currentInstructorId
      );

      setGroupGradeOverride(
        null
      );

      setGroupGradeOverrideScore(
        ''
      );

      setGroupGradeOverrideOpen(
        false
      );

      setSuccess(
        gradeOverrideStudentName +
          ' now uses the group grade.'
      );

    } catch (err) {
      console.error(
        'Removing individual group-grade override failed:',
        err
      );

      setError(
        err?.message ||
        'Could not remove the individual grade override.'
      );

    } finally {
      setGroupGradeOverrideSaving(
        false
      );
    }
  }


  async function handleSaveExtension() {
    if (
      target.type !== 'pd' &&
      target.type !==
        'assignment'
    ) {
      return;
    }

    const parsed =
      Number(extensionDays);

    if (
      !Number.isInteger(parsed) ||
      parsed < 0
    ) {
      setError(
        'Extension must be a whole number of days, 0 or greater.'
      );
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (
        target.type === 'pd'
      ) {
        let updatedRow;

        if (pdSub?.id) {
          const result =
            await updateSubmissionExtension(
              'pd_submissions',
              pdSub.id,
              parsed
            );

          updatedRow =
            result?.[0] || {
              ...pdSub,
              extension_days:
                parsed
            };

          onUpdate(
            'pd',
            pdSubmissions.map(
              p =>
                p.id ===
                pdSub.id
                  ? {
                      ...p,
                      ...updatedRow,
                      pd_feedback:
                        p.pd_feedback
                    }
                  : p
            )
          );
        } else {
          const result =
            await createPdExtensionRow(
              target.enrolmentId,
              target.weekId,
              parsed
            );

          updatedRow = {
            ...result[0],
            pd_feedback: null
          };

          onUpdate(
            'pd',
            [
              ...pdSubmissions,
              updatedRow
            ]
          );
        }
      } else {
        const structuredInstanceTarget =
          !!target
            .structuredFormInstanceId;

        const result =
          structuredInstanceTarget
            ? await saveStructuredFormInstanceExtensionRows(
                target.enrolmentId,
                target.assignmentId,
                target
                  .structuredFormInstanceId,
                parsed,
                structuredFormInstanceExtensions
              )
            : await saveAssignmentExtensionRows(
                target.enrolmentId,
                target.assignmentId,
                parsed
              );

        const returnedRows =
          Array.isArray(result)
            ? result
            : [];

        const returnedById =
          new Map(
            returnedRows.map(
              row => [
                String(row.id),
                row
              ]
            )
          );

        let nextAssignments =
          assignmentSubmissions.map(
            row => {
              const isParent =
                row.enrolment_id ===
                  target.enrolmentId &&
                row.course_assignment_id ===
                  target.assignmentId &&
                !row.course_assignment_schedule_item_id &&
                !row.structured_form_instance_id;

              if (!isParent) {
                return row;
              }

              return {
                ...row,

                ...(
                  returnedById.get(
                    String(row.id)
                  ) || {}
                ),

                ...(
                  structuredInstanceTarget
                    ? {}
                    : {
                        extension_days:
                          parsed
                      }
                ),

                assignment_feedback:
                  row.assignment_feedback
              };
            }
          );

        returnedRows.forEach(
          row => {
            if (
              !nextAssignments.some(
                existing =>
                  String(
                    existing.id
                  ) ===
                  String(
                    row.id
                  )
              )
            ) {
              nextAssignments.push({
                ...row,
                assignment_feedback:
                  null
              });
            }
          }
        );

        onUpdate(
          'assignment',
          nextAssignments
        );
      }

      setExtensionDays(
        String(parsed)
      );

      setSuccess(
        target.type ===
          'assignment' &&
        target.structuredFormInstanceId
          ? (
              parsed > 0
                ? `This Structured Form instance now has a ${parsed}-day instance-specific extension.`
                : 'Instance-specific extension removed.'
            )
          : (
              parsed > 0
                ? `Submission window extended by ${parsed} day${parsed === 1 ? '' : 's'}.`
                : 'Submission extension removed.'
            )
      );
    } catch (err) {
      console.error(
        'Extension save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save extension.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveSelfEval(submit = false, direction = null) {
    if (!selfEvalResponse?.id) {
      setError(
        'No self-evaluation submission found.'
      );
      return;
    }

    setSaving(true);
    setError('');

    try {
      const nextComments = submit
        ? {
            ...selfEvalComments,
            [SELF_EVAL_REVIEW_KEY]: new Date().toISOString()
          }
        : selfEvalComments;

      const result =
        await updateSelfEvalResponse(
          selfEvalResponse.id,
          {
            final_grade: String(finalGrade || '').trim() || null,
            instructor_comment: nextComments
          }
        );

      const updatedRow =
        result?.[0] || {
          ...selfEvalResponse,
          final_grade: String(finalGrade || '').trim() || null,
          instructor_comment: nextComments
        };

      onUpdate(
        'selfeval',
        selfEvalResponses.map(
          r =>
            r.id ===
            selfEvalResponse.id
              ? updatedRow
              : r
        )
      );

      setSelfEvalComments(nextComments);
      setSuccess(submit ? 'Completed.' : 'Saved.');

      if (submit) {
        if (direction === 'previous' && onSubmitPrevious) {
          onSubmitPrevious();
        } else if (direction === 'next' && onSubmitNext) {
          onSubmitNext();
        } else {
          onClose();
        }
      }
    } catch (err) {
      console.error(
        'Self-evaluation save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleClearUnpublishedGrade() {
    if (
      target.type !== 'assignment' ||
      !activeSubmissionId ||
      !currentInstructorId
    ) {
      return;
    }

    if (
      !window.confirm(
        'Clear this unpublished grade? It will return to the ungraded state and will not be included when grades are published.'
      )
    ) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await clearUnpublishedGrade(
        activeSubmissionId,
        currentInstructorId
      );

      setInstructorFeedbackRows(
        await fetchInstructorSubmissionFeedback(
          'assignment',
          activeSubmissionId
        )
      );

      setSuccess(
        'Unpublished grade cleared.'
      );

      onUpdate(
        'assignment',
        assignmentSubmissions
      );
    } catch (err) {
      console.error(
        'Clearing unpublished grade failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to clear unpublished grade.'
      );
    } finally {
      setSaving(false);
    }
  }


  async function handleUnsubmit() {
    if (
      (
        target.type === 'pd' ||
        target.type ===
          'assignment'
      ) &&
      deadlinePassed
    ) {
      setError(
        'Cannot unsubmit: the effective submission deadline has passed.'
      );
      return;
    }

    if (
      target.type === 'cq' &&
      getCqWindowStatus(week) !==
        'open'
    ) {
      setError(
        'Cannot unsubmit: the Critical Question submission window has closed.'
      );
      return;
    }

    const label =
      target.type === 'cq'
        ? 'Critical Question'
        : target.type === 'pd'
          ? 'philosophical diary entry'
          : target.type ===
              'assignment'
            ? 'assignment'
            : 'self-evaluation';

    const confirmationMessage =
      target.type === 'cq'
        ? `Unsubmit this ${label}? The submission record will be removed and the student will be able to submit a replacement while the window remains open.`
        : `Unsubmit this ${label}? The student's submission will become a draft again.`;

    if (
      !window.confirm(
        confirmationMessage
      )
    ) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (
        target.type === 'cq'
      ) {
        if (!cqSub?.id) {
          throw new Error(
            'Critical Question submission not found.'
          );
        }

        const alreadyAssigned =
          await cqHasQuestionImprovementAssignment(
            cqSub.id
          );

        if (alreadyAssigned) {
          setError(
            'Cannot unsubmit this Critical Question because it has already been assigned for Question Improvement.'
          );
          return;
        }

        await unsubmitCq(
          cqSub.id
        );

        onUpdate(
          'cq',
          (
            cqSubmissions || []
          ).filter(
            submission =>
              submission.id !==
              cqSub.id
          )
        );
      } else if (
        target.type === 'pd'
      ) {
        if (!pdSub?.id) {
          throw new Error(
            'PD submission not found.'
          );
        }

        const result =
          await unsubmitPd(
            pdSub.id
          );

        const updatedRow =
          result?.[0] || {
            ...pdSub,
            submitted: false,
            submitted_at: null
          };

        onUpdate(
          'pd',
          pdSubmissions.map(
            p =>
              p.id === pdSub.id
                ? {
                    ...p,
                    ...updatedRow,
                    pd_feedback:
                      p.pd_feedback
                  }
                : p
          )
        );
      } else if (
        target.type ===
        'assignment'
      ) {
        if (
          !assignmentSub?.id
        ) {
          throw new Error(
            'Assignment submission not found.'
          );
        }

        const result =
          await unsubmitAssignment(
            assignmentSub.id
          );

        const updatedRow =
          result?.submission?.[0] ||
          {
            ...assignmentSub,
            submitted: false,
            submitted_at: null
          };

        const updatedFeedback =
          result?.feedback?.[0]
            ? result.feedback[0]
            : assignmentFeedback
              ? {
                  ...assignmentFeedback,
                  submitted: false,
                  submitted_at: null,
                  student_response: null
                }
              : null;

        onUpdate(
          'assignment',
          assignmentSubmissions.map(
            s =>
              s.id ===
              assignmentSub.id
                ? {
                    ...s,
                    ...updatedRow,
                    assignment_feedback:
                      updatedFeedback
                  }
                : s
          )
        );
      } else if (
        target.type ===
        'selfeval'
      ) {
        if (
          !selfEvalResponse?.id
        ) {
          throw new Error(
            'Self-evaluation submission not found.'
          );
        }

        const result =
          await unsubmitSelfEval(
            selfEvalResponse.id
          );

        const updatedRow =
          result?.[0] || {
            ...selfEvalResponse,
            status: 'Draft',
            submitted_at: null
          };

        onUpdate(
          'selfeval',
          selfEvalResponses.map(
            r =>
              r.id ===
              selfEvalResponse.id
                ? updatedRow
                : r
          )
        );
      }

      onClose();
    } catch (err) {
      console.error(
        'Unsubmit failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to unsubmit.'
      );
    } finally {
      setSaving(false);
    }
  }

  function renderExtensionControls() {
    if (
      target.type !== 'pd' &&
      target.type !==
        'assignment'
    ) {
      return null;
    }

    const previewExtension =
      Number(extensionDays);

    const validPreview =
      Number.isInteger(
        previewExtension
      ) &&
      previewExtension >= 0;

    const previewEffectiveDays =
      target.type ===
        'assignment' &&
      target.structuredFormInstanceId &&
      validPreview
        ? Math.max(
            assignmentWideExtensionDays,
            previewExtension
          )
        : previewExtension;

    const previewEffective =
      originalDeadline &&
      validPreview
        ? addDays(
            originalDeadline,
            previewEffectiveDays
          )
        : effectiveDeadline;

    return (
      <div
        style={
          styles.extensionBox
        }
      >
        <div
          style={
            styles.extensionHeader
          }
        >
          Submission window
        </div>

        {target.type ===
          'assignment' &&
          target.scheduleItemId && (
          <div
            style={
              styles.extensionNote
            }
          >
            This extension is
            assignment-wide and applies
            to every linked occurrence
            for this student/group.
          </div>
        )}

        {target.type ===
          'assignment' &&
          target
            .structuredFormInstanceId && (
          <div
            style={
              styles.extensionNote
            }
          >
            This extension applies only
            to this Structured Form
            instance
            {target
              .assignmentGroupNumber
                ? ` for Assignment Group ${target.assignmentGroupNumber}`
                : ''}
            .
          </div>
        )}

        {target.type ===
          'assignment' &&
          target
            .structuredFormInstanceId &&
          assignmentWideExtensionDays >
            0 && (
          <div
            style={
              styles.extensionNote
            }
          >
            There is also a{' '}
            {
              assignmentWideExtensionDays
            }
            -day assignment-wide
            extension. The effective
            extension for this instance
            is whichever value is larger.
          </div>
        )}

        <div
          style={
            styles.deadlineLine
          }
        >
          Original deadline:{' '}
          <strong>
            {formatDeadline(
              originalDeadline
            )}
          </strong>
        </div>

        <div
          style={
            styles.deadlineLine
          }
        >
          Effective deadline:{' '}
          <strong>
            {formatDeadline(
              previewEffective
            )}
          </strong>
        </div>

        <div
          style={
            styles.extensionControls
          }
        >
          <label
            style={
              styles.extensionLabel
            }
          >
            {
              target.type ===
                'assignment' &&
              target
                .structuredFormInstanceId
                ? 'Instance extension (days)'
                : 'Extension (days)'
            }

            <input
              type="number"
              min="0"
              step="1"
              style={
                styles.extensionInput
              }
              value={
                extensionDays
              }
              onChange={e =>
                setExtensionDays(
                  e.target.value
                )
              }
              disabled={saving}
            />
          </label>

          <button
            type="button"
            style={
              styles.secondaryBtn
            }
            disabled={saving}
            onClick={
              handleSaveExtension
            }
          >
            {saving
              ? 'Saving...'
              : 'Save Extension'}
          </button>
        </div>

        {savedExtensionDays >
          0 && (
          <div
            style={
              styles.extensionNote
            }
          >
            {target.type ===
              'assignment' &&
            target
              .structuredFormInstanceId
              ? (
                  <>
                    This instance currently
                    has a{' '}
                    {
                      savedExtensionDays
                    }
                    -day instance-specific
                    extension.
                  </>
                )
              : (
                  <>
                    This {
                      target
                        .assignmentGroupNumber
                        ? 'group'
                        : 'student'
                    } currently has a{' '}
                    {
                      savedExtensionDays
                    }
                    -day extension.
                  </>
                )}
          </div>
        )}
      </div>
    );
  }


  async function handleReassignCqType() {
    if (!cqSub?.id || saving) {
      return;
    }

    const currentType =
      cqSub.question_type ===
        'Clarificatory'
        ? 'Clarificatory'
        : 'Critical';

    const nextType =
      currentType ===
        'Clarificatory'
        ? 'Critical'
        : 'Clarificatory';

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (
        currentType ===
          'Critical' &&
        nextType ===
          'Clarificatory'
      ) {
        const alreadyAssigned =
          await cqHasQuestionImprovementAssignment(
            cqSub.id
          );

        if (alreadyAssigned) {
          throw new Error(
            'This Critical Question has already been assigned for Question Improvement and cannot be changed to Clarificatory.'
          );
        }
      }

      const response =
        await fetch(
          SUPABASE_URL +
            '/rest/v1/cq_submissions?id=eq.' +
            encodeURIComponent(
              cqSub.id
            ),
          {
            method: 'PATCH',

            headers: {
              'apikey':
                SUPABASE_KEY,

              'Authorization':
                'Bearer ' +
                SUPABASE_KEY,

              'Content-Type':
                'application/json',

              'Prefer':
                'return=representation'
            },

            body:
              JSON.stringify({
                question_type:
                  nextType
              })
          }
        );

      if (!response.ok) {
        const detail =
          await response.text();

        throw new Error(
          'Could not reassign question (' +
            response.status +
            '): ' +
            detail
        );
      }

      const rows =
        await response.json();

      const updated =
        Array.isArray(rows) &&
        rows[0]
          ? rows[0]
          : {
              ...cqSub,
              question_type:
                nextType
            };

      onUpdate(
        'cq',
        (cqSubmissions || []).map(
          submission =>
            String(
              submission.id
            ) ===
              String(
                cqSub.id
              )
              ? {
                  ...submission,
                  ...updated
                }
              : submission
        )
      );

      setSuccess(
        'Reassigned as ' +
          nextType +
          ' Question.'
      );

    } catch (err) {
      setError(
        err?.message ||
        'Could not reassign question.'
      );

    } finally {
      setSaving(false);
    }
  }


  function renderCqPanel() {
    if (!cqSub) {
      return (
        <div
          style={
            styles.noSubmissionBox
          }
        >
          No Critical Question
          submission found.
        </div>
      );
    }

    const questionText =
      getCqQuestionText(cqSub);

    const windowOpen =
      getCqWindowStatus(
        week
      ) === 'open';

    return (
      <div>
        <div
          style={
            styles.submissionBox
          }
        >
          <SubmissionHeading
            submittedAt={
              cqSub.submitted_at
            }
          />

          {cqSub.question_type && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                marginBottom: '10px'
              }}
            >
              <div
                style={{
                  ...styles.cqType,
                  marginBottom: 0
                }}
              >
                {cqSub.question_type}{' '}
                Question
              </div>

              <button
                type="button"
                style={
                  styles.secondaryBtn
                }
                disabled={saving}
                onClick={
                  handleReassignCqType
                }
              >
                {cqSub.question_type ===
                  'Clarificatory'
                  ? 'Reassign as Critical Question'
                  : 'Reassign as Clarificatory Question'}
              </button>
            </div>
          )}

          <div
            style={
              styles.cqQuestionText
            }
          >
            {questionText ||
              '(No question text found in this submission record.)'}
          </div>
        </div>

        <div
          style={
            styles.unsubmitRow
          }
        >
          <button
            type="button"
            style={{
              ...styles.dangerBtn,
              ...(
                !windowOpen
                  ? styles.disabledDangerBtn
                  : {}
              )
            }}
            disabled={
              saving ||
              !windowOpen
            }
            onClick={
              handleUnsubmit
            }
            title={
              windowOpen
                ? 'Remove this submission so the student can submit again.'
                : 'The Critical Question submission window has closed.'
            }
          >
            Unsubmit Student
            Submission
          </button>

          {!windowOpen && (
            <div
              style={
                styles.unsubmitUnavailable
              }
            >
              Unsubmit unavailable:
              the Critical Question
              submission window has
              closed.
            </div>
          )}

          <div
            style={
              styles.cqUnsubmitNote
            }
          >
            Unsubmitting removes this
            CQ submission record. The
            student can then submit a
            replacement while the
            submission window remains
            open.
          </div>
        </div>
      </div>
    );
  }

  function renderPdPanel() {
    return (
      <div>
        {pdSub?.submitted ? (
          <div
            style={
              styles.submissionBox
            }
          >
            <SubmissionHeading
              submittedAt={
                pdSub.submitted_at
              }
            />

            <div
              style={
                styles.richTextDisplay
              }
              dangerouslySetInnerHTML={{
                __html:
                  pdSub.text ||
                  '(No content)'
              }}
            />
          </div>
        ) : (
          <div
            style={
              styles.noSubmissionBox
            }
          >
            Student has not submitted
            this entry yet.
          </div>
        )}

        {pdSub?.submitted && (
          <FeedbackForm
            comment={comment}
            setComment={
              setComment
            }
            studentResponse={
              pdFeedback
                ?.student_response ||
              ''
            }
            instructorNote={
              instructorNote
            }
            setInstructorNote={
              setInstructorNote
            }
            completionStatus={
              completionStatus
            }
            setCompletionStatus={
              setCompletionStatus
            }
            showScore={
              course?.grading_mode ===
              'traditional'
            }
            score={score}
            setScore={setScore}
            completionDisabled={
              completionLocked
            }
            scoreDisabled={
              scoreLocked
            }
            effectiveOutcomeText={
              effectiveOutcomeText
            }
            otherInstructorFeedback={
              otherInstructorFeedback
            }
            peerReviewSubmissionId={
              null
            }
            onSaveDraft={() =>
              handleSavePdFeedback(
                false
              )
            }
            onSubmit={() =>
              handleSavePdFeedback(
                true
              )
            }
            showSubmitNavigation
            onSubmitPrevious={
              onSubmitPrevious
                ? () =>
                    handleSavePdFeedback(
                      true,
                      'previous'
                    )
                : null
            }
            onSubmitNext={
              onSubmitNext
                ? () =>
                    handleSavePdFeedback(
                      true,
                      'next'
                    )
                : null
            }
            saving={saving}
          />
        )}

        {!target.structuredFormInstanceId && renderExtensionControls()}

        {pdSub?.submitted && (
          <div
            style={
              styles.unsubmitRow
            }
          >
            <button
              style={{
                ...styles.dangerBtn,
                ...(
                  deadlinePassed
                    ? styles.disabledDangerBtn
                    : {}
                )
              }}
              disabled={
                saving ||
                deadlinePassed
              }
              onClick={
                handleUnsubmit
              }
              title={
                deadlinePassed
                  ? 'The effective submission deadline has passed.'
                  : 'Return this submission to draft status.'
              }
            >
              Unsubmit Student
              Submission
            </button>

            {deadlinePassed && (
              <div
                style={
                  styles.unsubmitUnavailable
                }
              >
                Unsubmit unavailable:
                the effective
                submission deadline
                has passed.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  async function refreshAfterInstructorUpload() {
    const response =
      await fetch(
        SUPABASE_URL +
          '/rest/v1/assignment_submissions?course_assignment_id=eq.' +
          encodeURIComponent(
            target.assignmentId
          ) +
          '&select=*,assignment_feedback(*)',
        {
          headers: {
            'apikey':
              SUPABASE_KEY,

            'Authorization':
              'Bearer ' +
              SUPABASE_KEY
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        'Could not refresh assignment submissions.'
      );
    }

    const rows =
      await response.json();

    const untouched =
      (
        assignmentSubmissions ||
        []
      ).filter(
        row =>
          String(
            row.course_assignment_id
          ) !==
          String(
            target.assignmentId
          )
      );

    onUpdate(
      'assignment',
      [
        ...untouched,

        ...(
          Array.isArray(rows)
            ? rows
            : []
        )
      ]
    );

    setSuccess(
      'Submission uploaded on behalf of student.'
    );
  }


  function renderAssignmentPanel() {
    const structuredSnapshot =
      assignmentSub?.structured_form_snapshot;

    const hasMultiFiles =
      !!assignmentSub
        ?.file_source_submission_id;

    const hasFile =
      !!assignmentSub
        ?.drive_file_id;

    const hasText =
      !!assignmentSub?.text;

    const uploadProblem =
      !!(
        assignmentSub?.submitted &&
        !structuredSnapshot &&
        !hasFile &&
        !hasText &&
        String(
          assignmentSub?.file_name || ''
        ).trim()
      );

    return (
      <div>
        {target.structuredFormInstanceId &&
          target.structuredFormScheduleSourceLabel && (
          <div
            style={{
              marginBottom: '12px',
              padding: '10px 12px',
              border: '1px solid #d9e2ec',
              borderRadius: '7px',
              background: '#f7fafc',
              color: '#486581',
              fontSize: '12px',
              lineHeight: 1.55
            }}
          >
            <div>
              <strong>Schedule anchor:</strong>{' '}
              {target.structuredFormScheduleSourceLabel}
            </div>

            <div>
              <strong>Instance window:</strong>{' '}
              {formatDateOnly(
                target.structuredFormOpenDate
              )}
              {' – '}
              {formatDateOnly(
                target.structuredFormCloseDate
              )}
              {' before extension'}
            </div>
          </div>
        )}

        {target.structuredFormInstanceId && structuredSnapshots.length > 0 && (
          <label style={styles.structuredVersionLabel}>
            Submitted version
            <select
              style={styles.structuredVersionSelect}
              value={selectedStructuredSnapshotId || assignmentSub?.id || ''}
              onChange={event => setSelectedStructuredSnapshotId(event.target.value)}
            >
              {structuredSnapshots.map(snapshot => (
                <option key={snapshot.id} value={snapshot.id}>
                  Instance {snapshot.structured_form_snapshot?.instance?.instance_order || '?'} — {formatDeadline(snapshot.submitted_at)}
                </option>
              ))}
            </select>
          </label>
        )}

        {assignmentSub?.submitted ? (
          <div
            style={
              styles.submissionBox
            }
          >
            <SubmissionHeading
              label={
                target.assignmentGroupNumber
                  ? 'Group Submission'
                  : 'Student Submission'
              }
              submittedAt={
                assignmentSub.submitted_at
              }
            />

            {structuredSnapshot && (
              <StructuredSnapshotView
                snapshot={structuredSnapshot}
              />
            )}

            {!structuredSnapshot && hasText && (
              <div
                style={
                  styles.richTextDisplay
                }
                dangerouslySetInnerHTML={{
                  __html:
                    assignmentSub.text
                }}
              />
            )}

            {!structuredSnapshot &&
              hasMultiFiles && (
              <div
                style={{
                  marginTop:
                    '12px'
                }}
              >
                <div
                  style={
                    styles.submissionLabel
                  }
                >
                  Submitted files
                </div>

                <MultiFileGradingViewer
                  submission={
                    assignmentSub
                  }
                />
              </div>
            )}

            {!structuredSnapshot &&
              !hasMultiFiles &&
              hasFile && (
              <div
                style={{
                  marginTop:
                    '12px'
                }}
              >
                <div
                  style={
                    styles.submissionLabel
                  }
                >
                  Uploaded file
                </div>

                {/\.(pdf|docx)$/i.test(
                  String(
                    assignmentSub.file_name ||
                    ''
                  )
                ) ? (
                  <GradingPdfViewer
                    submissionId={
                      assignmentSub.id
                    }
                    fileName={
                      assignmentSub.file_name
                    }
                    reviewPdfDriveFileId={
                      assignmentSub
                        .review_pdf_drive_file_id
                    }
                  refreshToken={assignmentSub.submitted_at || assignmentSub.updated_at || assignmentSub.drive_file_id}
                  />
                ) : (
                  <iframe
                    src={
                      'https://drive.google.com/file/d/' +
                      assignmentSub.drive_file_id +
                      '/preview'
                    }
                    style={
                      styles.docPreview
                    }
                    title="Student submission"
                  />
                )}

                {!/\.pdf$/i.test(
                  String(
                    assignmentSub.file_name ||
                    ''
                  )
                ) && (
                  <a
                    href={
                      'https://drive.google.com/uc?export=download&id=' +
                      assignmentSub.drive_file_id
                    }
                    target="_blank"
                    rel="noreferrer"
                    style={
                      styles.downloadLink
                    }
                  >
                    Download original document
                  </a>
                )}
              </div>
            )}

            {!structuredSnapshot &&
              !hasFile &&
              !hasText && (
              uploadProblem ? (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '10px 12px',
                    border:
                      '1px solid #e6b8b8',
                    borderRadius:
                      '7px',
                    background:
                      '#fff5f5',
                    color:
                      '#8a1f1f',
                    fontSize:
                      '12px',
                    lineHeight:
                      1.5
                  }}
                >
                  <strong>
                    Upload incomplete.
                  </strong>{' '}

                  This record was marked
                  submitted, but no uploaded
                  file is attached to it.

                  {assignmentSub
                    ?.file_name && (
                    <div
                      style={{
                        marginTop:
                          '4px'
                      }}
                    >
                      Expected file:{' '}

                      <strong>
                        {
                          assignmentSub
                            .file_name
                        }
                      </strong>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={
                    styles.muted
                  }
                >
                  (No content)
                </div>
              )
            )}

            <AiDisclosureReadOnly
              submission={assignmentSub}
            />
          </div>
        ) : (
          <div
            style={
              styles.noSubmissionBox
            }
          >
            {target.assignmentGroupNumber
              ? 'This group has not submitted this assignment yet.'
              : 'Student has not submitted this assignment yet.'}
          </div>
        )}

        <InstructorAssignmentUpload
          courseId={
            course.id
          }
          courseAssignment={
            courseAssignment
          }
          target={
            target
          }
          student={
            student
          }
          submission={
            assignmentSub
          }
          currentInstructorId={
            currentInstructorId
          }
          onSubmitted={
            refreshAfterInstructorUpload
          }
        />

        {assignmentSub
          ?.submitted && (
          <FeedbackForm
            comment={comment}
            setComment={
              setComment
            }
            studentResponse={
              assignmentFeedback
                ?.student_response ||
              ''
            }
            instructorNote={
              instructorNote
            }
            setInstructorNote={
              setInstructorNote
            }
            completionStatus={
              completionStatus
            }
            setCompletionStatus={
              setCompletionStatus
            }
            showScore={
              course?.grading_mode ===
              'traditional'
            }
            score={score}
            setScore={setScore}
            completionDisabled={
              completionLocked
            }
            scoreDisabled={
              scoreLocked
            }
            scoreExtra={
              isGroupAssignment ? (
                <div
                  style={
                    styles
                      .groupGradeOverrideArea
                  }
                >
                  {groupGradeOverride &&
                    !groupGradeOverrideOpen && (
                    <div
                      style={
                        styles
                          .groupGradeOverrideCurrent
                      }
                    >
                      Individual grade for{' '}
                      <strong>
                        {
                          gradeOverrideStudentName
                        }
                      </strong>
                      :{' '}
                      <strong>
                        {Number(
                          groupGradeOverride
                            .score
                        )}%
                      </strong>
                    </div>
                  )}

                  {!groupGradeOverrideOpen ? (
                    <button
                      type="button"
                      style={
                        styles
                          .groupGradeOverrideButton
                      }
                      disabled={
                        groupGradeOverrideSaving
                      }
                      onClick={() => {
                        setGroupGradeOverrideScore(
                          groupGradeOverride
                            ?.score !==
                              null &&
                          groupGradeOverride
                            ?.score !==
                              undefined
                            ? String(
                                groupGradeOverride
                                  .score
                              )
                            : ''
                        );

                        setGroupGradeOverrideOpen(
                          true
                        );
                      }}
                    >
                      Overwrite group grade
                    </button>

                  ) : (
                    <div
                      style={
                        styles
                          .groupGradeOverrideEditor
                      }
                    >
                      <label
                        style={
                          styles
                            .groupGradeOverrideLabel
                        }
                      >
                        Individual score for{' '}
                        {
                          gradeOverrideStudentName
                        }
                      </label>

                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        style={
                          styles.select
                        }
                        value={
                          groupGradeOverrideScore
                        }
                        disabled={
                          groupGradeOverrideSaving
                        }
                        onChange={
                          event =>
                            setGroupGradeOverrideScore(
                              event.target.value
                            )
                        }
                        placeholder="0–100"
                      />

                      <div
                        style={
                          styles
                            .groupGradeOverrideActions
                        }
                      >
                        <button
                          type="button"
                          style={
                            styles.secondaryBtn
                          }
                          disabled={
                            groupGradeOverrideSaving
                          }
                          onClick={
                            handleSaveGroupGradeOverride
                          }
                        >
                          {groupGradeOverrideSaving
                            ? 'Saving...'
                            : 'Save override'}
                        </button>

                        {groupGradeOverride && (
                          <button
                            type="button"
                            style={
                              styles.dangerBtn
                            }
                            disabled={
                              groupGradeOverrideSaving
                            }
                            onClick={
                              handleRemoveGroupGradeOverride
                            }
                          >
                            Use group grade
                          </button>
                        )}

                        <button
                          type="button"
                          style={
                            styles.secondaryBtn
                          }
                          disabled={
                            groupGradeOverrideSaving
                          }
                          onClick={() => {
                            setGroupGradeOverrideOpen(
                              false
                            );

                            setGroupGradeOverrideScore(
                              groupGradeOverride
                                ?.score !==
                                  null &&
                              groupGradeOverride
                                ?.score !==
                                  undefined
                                ? String(
                                    groupGradeOverride
                                      .score
                                  )
                                : ''
                            );
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null
            }
            effectiveOutcomeText={
              effectiveOutcomeText
            }
            otherInstructorFeedback={
              otherInstructorFeedback
            }
            peerReviewSubmissionId={
              assignmentSub?.id ||
              null
            }
            responseLabel={
              target.assignmentGroupNumber
                ? 'Group Response'
                : 'Student Response'
            }
            onSaveDraft={() =>
              handleSaveAssignmentFeedback(
                false
              )
            }
            onSubmit={() =>
              handleSaveAssignmentFeedback(
                true
              )
            }
            showSubmitNavigation
            onSubmitPrevious={
              onSubmitPrevious
                ? () =>
                    handleSaveAssignmentFeedback(
                      true,
                      'previous'
                    )
                : null
            }
            onSubmitNext={
              onSubmitNext
                ? () =>
                    handleSaveAssignmentFeedback(
                      true,
                      'next'
                    )
                : null
            }
            saving={saving}
          />
        )}

        {!assignmentSub
          ?.submitted &&
          assignmentFeedback && (
          <div
            style={
              styles.retainedFeedbackBox
            }
          >
            <div
              style={
                styles.retainedFeedbackTitle
              }
            >
              Retained instructor
              feedback
            </div>

            <div
              style={
                styles.retainedFeedbackNote
              }
            >
              This feedback is
              currently a draft and is
              not visible to the
              student. It will remain
              here for reference until
              the student resubmits.
            </div>

            {assignmentFeedback.comment && (
              <div
                style={
                  styles.retainedFeedbackContent
                }
                dangerouslySetInnerHTML={{
                  __html:
                    assignmentFeedback.comment
                }}
              />
            )}

            {assignmentFeedback
              .completion_status && (
              <div
                style={
                  styles.retainedFeedbackStatus
                }
              >
                Completion status:{' '}
                <strong>
                  {
                    assignmentFeedback
                      .completion_status
                  }
                </strong>
              </div>
            )}
          </div>
        )}

        {renderExtensionControls()}

        {assignmentSub
          ?.submitted && (
          <div
            style={
              styles.unsubmitRow
            }
          >
            {hasUnpublishedGrade &&
            !hasPublishedGrade ? (
              <button
                type="button"
                style={
                  styles.dangerBtn
                }
                disabled={saving}
                onClick={
                  handleClearUnpublishedGrade
                }
                title="Return this unpublished grade to the ungraded state."
              >
                Clear Grade
              </button>
            ) : (
              <>
                <button
                  style={{
                    ...styles.dangerBtn,
                    ...(
                      deadlinePassed
                        ? styles.disabledDangerBtn
                        : {}
                    )
                  }}
                  disabled={
                    saving ||
                    deadlinePassed
                  }
                  onClick={
                    handleUnsubmit
                  }
                  title={
                    deadlinePassed
                      ? 'The effective submission deadline has passed.'
                      : 'Return this submission to draft status.'
                  }
                >
                  {target.assignmentGroupNumber
                    ? 'Unsubmit Group Submission'
                    : 'Unsubmit Student Submission'}
                </button>

                {deadlinePassed && (
                  <div
                    style={
                      styles.unsubmitUnavailable
                    }
                  >
                    Unsubmit unavailable:
                    the effective
                    submission deadline
                    has passed.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderSelfEvalPanel() {
    if (!selfEvalResponse) {
      return (
        <div
          style={
            styles.noSubmissionBox
          }
        >
          No self-evaluation
          submission found.
        </div>
      );
    }

    if (loading) {
      return (
        <div
          style={styles.muted}
        >
          Loading questions...
        </div>
      );
    }

    const answers =
      selfEvalResponse.answers ||
      {};

    const snapshot =
      selfEvalResponse
        .form_snapshot;

    const questions =
      snapshot?.questions ||
      selfEvalQuestions;

    const gradeQuestion =
      questions.find(
        q => q.type === 'GRADE'
      );

    const studentGrade =
      gradeQuestion
        ? answers[
            gradeQuestion.id
          ]?.value || '—'
        : answers?.grade || '—';

    return (
      <div>
        <section style={{ marginBottom: '22px', minWidth: 0 }}>
          <h3 style={{
            margin: '0 0 10px',
            fontSize: '16px'
          }}>
            Student Progress to Date
          </h3>

          <StudentProgress
            course={course}
            session={session}
            embeddedEnrolmentId={target.enrolmentId}
          />
        </section>

        <div
          style={styles.gradeRow}
        >
          <div
            style={styles.gradeBox}
          >
            <div
              style={
                styles.gradeLabel
              }
            >
              Student suggested
              grade
            </div>

            <div
              style={
                styles.gradeValue
              }
            >
              {studentGrade}
            </div>
          </div>

          {selfEvalReviewIsComplete(selfEvalResponse) && (
            <div style={styles.gradeBox}>
              <div style={styles.gradeLabel}>Review status</div>
              <div style={styles.gradeValue}>
                {selfEvalCompleteGradeLabel({
                  ...selfEvalResponse,
                  final_grade: finalGrade
                })}
              </div>
            </div>
          )}

          <div
            style={styles.gradeBox}
          >
            <div
              style={
                styles.gradeLabel
              }
            >
              Final grade
            </div>

            <input
              style={
                styles.gradeInput
              }
              type="text"
              value={finalGrade}
              onChange={e =>
                setFinalGrade(
                  e.target.value
                )
              }
              placeholder="Enter final grade"
            />
          </div>
        </div>

        <div
          style={
            styles.questionList
          }
        >
          {questions
            .filter(
              q =>
                q.type !== 'GRADE'
            )
            .map(q => {
              const answer =
                answers[q.id] || {};

              return (
                <div
                  key={q.id}
                  style={
                    styles.questionItem
                  }
                >
                  <div
                    style={
                      styles.questionTitle
                    }
                  >
                    {
                      q.section_title
                    }
                  </div>

                  <div
                    style={
                      styles.questionPrompt
                    }
                  >
                    {q.prompt}
                  </div>

                  <div
                    style={
                      styles.answerBox
                    }
                  >
                    {answer.value ||
                      '(No answer)'}
                  </div>

                  {q.show_justification &&
                    answer.justification && (
                    <div
                      style={
                        styles.answerBox
                      }
                    >
                      {
                        answer.justification
                      }
                    </div>
                  )}

                  <div
                    style={
                      styles.commentField
                    }
                  >
                    <label
                      style={
                        styles.smallLabel
                      }
                    >
                      Instructor
                      comment
                      (internal)
                    </label>

                    <RichTextEditor
                      value={
                        selfEvalComments[
                          q.id
                        ] || ''
                      }
                      onChange={val =>
                        setSelfEvalComments(
                          prev => ({
                            ...prev,
                            [q.id]:
                              val
                          })
                        )
                      }
                    />
                  </div>
                </div>
              );
            })}

          {courseSpecificQuestion && (
            <div
              style={
                styles.questionItem
              }
            >
              <div
                style={
                  styles.questionTitle
                }
              >
                {
                  courseSpecificQuestion
                    .section_title
                }
              </div>

              <div
                style={
                  styles.questionPrompt
                }
              >
                {
                  courseSpecificQuestion
                    .prompt
                }
              </div>

              <div
                style={
                  styles.answerBox
                }
              >
                {answers
                  .courseSpecific
                  ?.value ||
                  '(No answer)'}
              </div>

              <div
                style={
                  styles.commentField
                }
              >
                <label
                  style={
                    styles.smallLabel
                  }
                >
                  Instructor comment
                  (internal)
                </label>

                <RichTextEditor
                  value={
                    selfEvalComments[
                      'courseSpecific'
                    ] || ''
                  }
                  onChange={val =>
                    setSelfEvalComments(
                      prev => ({
                        ...prev,
                        courseSpecific:
                          val
                      })
                    )
                  }
                />
              </div>
            </div>
          )}

          {gradeQuestion && (
            <div
              style={
                styles.questionItem
              }
            >
              <div
                style={
                  styles.questionTitle
                }
              >
                {
                  gradeQuestion
                    .section_title
                }
              </div>

              <div
                style={
                  styles.questionPrompt
                }
              >
                {
                  gradeQuestion.prompt
                }
              </div>

              <div
                style={
                  styles.answerBox
                }
              >
                {answers[
                  gradeQuestion.id
                ]?.value || '—'}
              </div>

              {gradeQuestion
                .show_justification && (
                <div
                  style={
                    styles.answerBox
                  }
                >
                  {answers[
                    gradeQuestion.id
                  ]?.justification ||
                    '—'}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          style={
            styles.actionRow
          }
        >
          <button
            style={styles.secondaryBtn}
            disabled={saving}
            onClick={() => handleSaveSelfEval(false)}
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>

          <button
            style={{
              ...styles.secondaryBtn,
              ...(!onSubmitPrevious ? styles.disabledSubmitNavBtn : {})
            }}
            disabled={saving || !onSubmitPrevious}
            onClick={() => handleSaveSelfEval(true, 'previous')}
          >
            Submit (Previous)
          </button>

          <button
            style={styles.btn}
            disabled={saving}
            onClick={() => handleSaveSelfEval(true)}
          >
            {saving ? 'Submitting...' : 'Submit'}
          </button>

          <button
            style={{
              ...styles.secondaryBtn,
              ...(!onSubmitNext ? styles.disabledSubmitNavBtn : {})
            }}
            disabled={saving || !onSubmitNext}
            onClick={() => handleSaveSelfEval(true, 'next')}
          >
            Submit (Next)
          </button>

          <button
            style={
              styles.dangerBtn
            }
            disabled={saving}
            onClick={
              handleUnsubmit
            }
          >
            Unsubmit
          </button>
        </div>
      </div>
    );
  }

  function getTitle() {
    const name =
      getStudentName(student);

    if (
      target.type === 'pd'
    ) {
      return `PD — ${name} — Week ${week?.week_number}`;
    }

    if (
      target.type === 'cq'
    ) {
      return `CQ — ${name} — Week ${week?.week_number}`;
    }

    if (
      target.type ===
      'selfeval'
    ) {
      return `Self-Evaluation (${target.section}) — ${name}`;
    }

    if (
      target.type ===
      'assignment'
    ) {
      const ca =
        courseAssignments?.find(
          a =>
            a.id ===
            target.assignmentId
        );

      const label = ca
        ? (
            (
              ca.assignment_types
                ?.name ||
              'Assignment'
            ) +
            (
              ca.instance_number > 1
                ? ` ${ca.instance_number}`
                : ''
            )
          )
        : 'Assignment';

      const occurrenceSuffix = target.scheduleSourceTitle
        ? ` — ${target.scheduleSourceTitle}`
        : target.structuredFormInstanceId
          ? ` — Instance ${assignmentSub?.structured_form_snapshot?.instance?.instance_order || target.structuredFormInstanceOrder || '?'}`
          : '';

      // GRADING_HEADER_STUDENT_ID_V1
      const studentId =
        String(
          student?.student_code ||
          ''
        ).trim();

      const studentLabel =
        studentId &&
        studentId !== name
          ? `${name} ${studentId}`
          : name;

      const ownerLabel =
        target.assignmentGroupNumber
          ? `Assignment Group ${target.assignmentGroupNumber}`
          : studentLabel;

      return `${label} — ${ownerLabel}${occurrenceSuffix}`;
    }

    return 'Grading';
  }

  return (
    <div
      ref={overlayRef}
      style={styles.overlay}
    >
      <div
        style={styles.panel}
      >
        <div
          style={
            styles.panelHeader
          }
        >
          <h2
            style={
              styles.panelTitle
            }
          >
            {getTitle()}
          </h2>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
            marginLeft: '12px'
          }}>
            {target.type === 'selfeval' &&
              student?.photo_drive_file_id && (
              <img
                key={student.photo_drive_file_id}
                src={`https://drive.google.com/thumbnail?id=${encodeURIComponent(student.photo_drive_file_id)}&sz=w200`}
                alt={getStudentName(student)}
                style={{
                  width: '48px',
                  height: '60px',
                  objectFit: 'cover',
                  objectPosition: 'center top',
                  border: '1px solid #d7dee5',
                  borderRadius: '5px',
                  display: 'block'
                }}
                onError={event => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            )}

            <button
              style={
                styles.closeBtn
              }
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        {error && (
          <div
            style={styles.error}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={styles.success}
          >
            {success}
          </div>
        )}

        <div
          style={
            styles.panelBody
          }
        >
          {target.type ===
            'cq' &&
            renderCqPanel()}

          {target.type ===
            'pd' &&
            renderPdPanel()}

          {target.type ===
            'assignment' &&
            renderAssignmentPanel()}

          {target.type ===
            'selfeval' &&
            renderSelfEvalPanel()}
        </div>
      </div>
    </div>
  );
}

function FeedbackForm({
  comment,
  setComment,
  studentResponse,
  instructorNote,
  setInstructorNote,
  completionStatus,
  setCompletionStatus,
  showScore = false,
  score,
  setScore,
  completionDisabled = false,
  scoreDisabled = false,
  scoreExtra = null,
  effectiveOutcomeText = '',
  otherInstructorFeedback = [],
  peerReviewSubmissionId = null,
  responseLabel = 'Student Response',
  onSaveDraft,
  onSubmit,
  showSubmitNavigation = false,
  onSubmitPrevious = null,
  onSubmitNext = null,
  saving
}) {
  return (
    <div
      style={
        styles.feedbackForm
      }
    >
      <div
        style={styles.field}
      >
        <label
          style={
            styles.sectionLabel
          }
        >
          Comment
        </label>

        <div
          style={
            styles.fieldHint
          }
        >
          Visible to the student
          only after feedback is
          submitted
        </div>

        <RichTextEditor
          value={comment}
          onChange={setComment}
        />
      </div>

      {otherInstructorFeedback.length > 0 && (
        <div
          style={
            styles.otherFeedbackSection
          }
        >
          <div
            style={
              styles.otherFeedbackHeading
            }
          >
            Other instructor feedback
          </div>

          {otherInstructorFeedback.map(
            feedback => (
              <div
                key={
                  feedback.id
                }
                style={
                  styles.otherFeedbackCard
                }
              >
                <div
                  style={
                    styles.otherFeedbackName
                  }
                >
                  {instructorFeedbackName(
                    feedback
                  )}
                </div>

                {feedback.comment && (
                  <div
                    style={
                      styles.otherFeedbackComment
                    }
                    dangerouslySetInnerHTML={{
                      __html:
                        feedback.comment
                    }}
                  />
                )}

                <div
                  style={
                    styles.otherFeedbackMeta
                  }
                >
                  {feedback.score !==
                    null &&
                    feedback.score !==
                      undefined && (
                    <span>
                      Score:{' '}
                      <strong>
                        {Number(
                          feedback.score
                        )}%
                      </strong>
                    </span>
                  )}

                  {feedback
                    .completion_status && (
                    <span>
                      Status:{' '}
                      <strong>
                        {
                          feedback
                            .completion_status
                        }
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      )}

      {peerReviewSubmissionId && (
        <ReceivedPeerReviews
          targetSubmissionId={
            peerReviewSubmissionId
          }
        />
      )}

      <div
        style={styles.field}
      >
        <label
          style={
            styles.sectionLabel
          }
        >
          {responseLabel}
        </label>

        {studentResponse ? (
          <div
            style={
              styles.studentResponseBox
            }
            dangerouslySetInnerHTML={{
              __html:
                studentResponse
            }}
          />
        ) : (
          <div
            style={
              styles.noResponseBox
            }
          >
            No response yet.
          </div>
        )}
      </div>

      <div
        style={styles.field}
      >
        <label
          style={
            styles.sectionLabel
          }
        >
          Instructor's Notes
        </label>

        <div
          style={
            styles.fieldHint
          }
        >
          Internal only
        </div>

        <RichTextEditor
          value={instructorNote}
          onChange={
            setInstructorNote
          }
        />
      </div>

      {effectiveOutcomeText && (
        <div
          style={
            styles.effectiveOutcome
          }
        >
          {effectiveOutcomeText}
        </div>
      )}

      <div
        style={styles.field}
      >
        <label
          style={
            styles.smallLabel
          }
        >
          Completion status
        </label>

        <select
          style={styles.select}
          value={
            completionStatus
          }
          disabled={
            completionDisabled
          }
          onChange={e =>
            setCompletionStatus(
              e.target.value
            )
          }
        >
          <option value="">
            — Not set —
          </option>

          <option value="Complete">
            Complete
          </option>

          <option value="Incomplete">
            Incomplete
          </option>
        </select>
      </div>

      {showScore && (
        <div style={styles.field}>
          <label style={styles.smallLabel}>
            Score (%)
          </label>

          <div style={styles.fieldHint}>
            Traditional grade for this assignment
          </div>

          <input
            type="number"
            min="0"
            max="100"
            step="0.1"
            style={styles.select}
            value={score ?? ''}
            disabled={
              scoreDisabled
            }
            onChange={event =>
              setScore(event.target.value)
            }
            placeholder="0–100"
          />

          {scoreExtra}
        </div>
      )}

      <div
        style={
          styles.actionRow
        }
      >
        <button
          style={
            styles.secondaryBtn
          }
          disabled={saving}
          onClick={onSaveDraft}
        >
          {saving
            ? 'Saving...'
            : 'Save Draft'}
        </button>

        {showSubmitNavigation && (
          <button
            style={{
              ...styles.secondaryBtn,
              ...(!onSubmitPrevious
                ? styles.disabledSubmitNavBtn
                : {})
            }}
            disabled={
              saving ||
              !onSubmitPrevious
            }
            onClick={
              onSubmitPrevious ||
              undefined
            }
          >
            Submit (Previous)
          </button>
        )}

        <button
          style={styles.btn}
          disabled={saving}
          onClick={onSubmit}
        >
          {saving
            ? 'Submitting...'
            : 'Submit Feedback'}
        </button>

        {showSubmitNavigation && (
          <button
            style={{
              ...styles.secondaryBtn,
              ...(!onSubmitNext
                ? styles.disabledSubmitNavBtn
                : {})
            }}
            disabled={
              saving ||
              !onSubmitNext
            }
            onClick={
              onSubmitNext ||
              undefined
            }
          >
            Submit (Next)
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background:
      'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 200,
    padding: '12px',
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch'
  },

  panel: {
    background: 'white',
    borderRadius: '12px',
    width: 'calc(100vw - 24px)',
    maxWidth: '1440px',
    minHeight: 'calc(100vh - 24px)',
    height: 'auto',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    boxShadow:
      '0 18px 60px rgba(0,0,0,0.25)'
  },

  panelHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 12,
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    padding: '18px 24px',
    borderBottom:
      '1px solid #d7dee5',
    background: 'white',
    borderRadius: '12px 12px 0 0',
    flexShrink: 0
  },

  panelTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#1f2933'
  },

  closeBtn: {
    border: 'none',
    background: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#66788a',
    lineHeight: 1
  },

  panelBody: {
    padding: '24px',
    overflow: 'visible',
    flex: '0 0 auto',
    minWidth: 0
  },

  muted: {
    color: '#66788a',
    fontSize: '14px'
  },

  sectionLabel: {
    display: 'block',
    fontWeight: '800',
    fontSize: '14px',
    color: '#1f2933',
    marginBottom: '2px'
  },

  otherFeedbackSection: {
    margin:
      '-4px 0 18px',
    padding:
      '12px 14px',
    border:
      '1px solid #d7dee5',
    borderRadius:
      '8px',
    background:
      '#f8fafb'
  },

  otherFeedbackHeading: {
    fontSize:
      '13px',
    fontWeight:
      '800',
    color:
      '#42515f',
    marginBottom:
      '9px'
  },

  otherFeedbackCard: {
    padding:
      '10px 0',
    borderBottom:
      '1px solid #e1e7ec'
  },

  otherFeedbackName: {
    fontSize:
      '12px',
    fontWeight:
      '800',
    color:
      '#215b7d',
    marginBottom:
      '5px'
  },

  otherFeedbackComment: {
    fontSize:
      '14px',
    color:
      '#1f2933',
    lineHeight:
      '1.5'
  },

  otherFeedbackMeta: {
    display:
      'flex',
    gap:
      '14px',
    flexWrap:
      'wrap',
    marginTop:
      '7px',
    fontSize:
      '12px',
    color:
      '#66788a'
  },

  effectiveOutcome: {
    margin:
      '4px 0 16px',
    padding:
      '9px 11px',
    border:
      '1px solid #c8d8e5',
    borderRadius:
      '7px',
    background:
      '#f0f6fa',
    color:
      '#215b7d',
    fontSize:
      '13px',
    fontWeight:
      '700'
  },

  fieldHint: {
    color: '#82909c',
    fontSize: '11px',
    marginBottom: '7px'
  },

  studentResponseBox: {
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    background: '#f7f9fa',
    padding: '12px',
    fontSize: '14px',
    lineHeight: '1.55'
  },

  noResponseBox: {
    border:
      '1px dashed #c8d2dc',
    borderRadius: '8px',
    background: '#f7f9fa',
    padding: '12px',
    color: '#82909c',
    fontSize: '13px'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    margin: '0 24px 0'
  },

  success: {
    background: '#e7f5ed',
    border:
      '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '14px',
    margin: '0 24px 0'
  },

  noSubmissionBox: {
    border:
      '1px dashed #c8d2dc',
    borderRadius: '10px',
    background: '#f7f9fa',
    padding: '16px',
    marginBottom: '18px',
    color: '#66788a',
    fontSize: '13px'
  },

  aiDisclosureBox: {
    marginTop: '16px',
    paddingTop: '14px',
    borderTop: '1px solid #d7dee5'
  },
  aiDisclosureTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
  },
  aiDisclosureRow: {
    fontSize: '13px',
    color: '#42515f',
    lineHeight: '1.55',
    marginBottom: '4px',
    overflowWrap: 'anywhere'
  },
  aiDisclosureLink: {
    color: '#215b7d',
    textDecoration: 'underline',
    overflowWrap: 'anywhere'
  },
  submissionBox: {
    background: '#f7f9fa',
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '20px'
  },

  submissionLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a',
    textTransform:
      'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
  },

  richTextDisplay: {
    fontSize: '14px',
    color: '#1f2933',
    lineHeight: '1.6',
    overflowWrap: 'anywhere'
  },

  unsubmitRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '10px',
    margin: '-8px 0 18px',
    flexWrap: 'wrap'
  },

  unsubmitUnavailable: {
    fontSize: '12px',
    color: '#66788a'
  },

  disabledDangerBtn: {
    color: '#9aa5b1',
    borderColor: '#d7dee5',
    background: '#f0f4f7',
    cursor: 'not-allowed',
    opacity: 0.75
  },

  extensionBox: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    background: '#f7f9fa',
    padding: '14px',
    marginBottom: '18px'
  },

  extensionHeader: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933',
    marginBottom: '8px'
  },

  deadlineLine: {
    fontSize: '13px',
    color: '#42515f',
    marginBottom: '4px'
  },

  extensionControls: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    marginTop: '12px'
  },

  extensionLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a'
  },

  extensionInput: {
    width: '110px',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '14px',
    background: 'white'
  },

  extensionNote: {
    fontSize: '12px',
    color: '#215b7d',
    marginTop: '9px'
  },

  retainedFeedbackBox: {
    border:
      '1px solid #b0cfe0',
    borderRadius: '10px',
    background: '#eef6fa',
    padding: '14px',
    marginBottom: '18px'
  },

  retainedFeedbackTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#215b7d',
    marginBottom: '5px'
  },

  retainedFeedbackNote: {
    fontSize: '12px',
    color: '#66788a',
    lineHeight: '1.5',
    marginBottom: '10px'
  },

  retainedFeedbackContent: {
    fontSize: '14px',
    color: '#1f2933',
    lineHeight: '1.6',
    overflowWrap: 'anywhere'
  },

  retainedFeedbackStatus: {
    fontSize: '12px',
    color: '#215b7d',
    marginTop: '8px'
  },

  docPreview: {
    width: '100%',
    height: '650px',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    marginTop: '8px'
  },

  downloadLink: {
    display: 'inline-block',
    marginTop: '8px',
    color: '#215b7d',
    fontSize: '13px',
    textDecoration:
      'underline'
  },

  feedbackForm: {
    marginTop: '4px'
  },

  field: {
    marginBottom: '16px'
  },

  smallLabel: {
    display: 'block',
    fontWeight: '700',
    fontSize: '12px',
    color: '#66788a',
    marginBottom: '6px',
    textTransform:
      'uppercase',
    letterSpacing: '0.05em'
  },

  select: {
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '14px',
    background: 'white',
    width: '200px'
  },

  groupGradeOverrideArea: {
    marginTop:
      '9px',
    display:
      'grid',
    gap:
      '7px',
    justifyItems:
      'start'
  },

  groupGradeOverrideCurrent: {
    fontSize:
      '12px',
    color:
      '#215b7d'
  },

  groupGradeOverrideButton: {
    border:
      '1px solid #9fb3c8',
    borderRadius:
      '6px',
    background:
      'white',
    color:
      '#215b7d',
    padding:
      '5px 8px',
    fontSize:
      '11px',
    fontWeight:
      '700',
    cursor:
      'pointer'
  },

  groupGradeOverrideEditor: {
    display:
      'grid',
    gap:
      '7px',
    padding:
      '10px',
    border:
      '1px solid #c8d8e5',
    borderRadius:
      '8px',
    background:
      '#f7f9fa'
  },

  groupGradeOverrideLabel: {
    fontSize:
      '12px',
    fontWeight:
      '700',
    color:
      '#42515f'
  },

  groupGradeOverrideActions: {
    display:
      'flex',
    gap:
      '7px',
    flexWrap:
      'wrap'
  },

  actionRow: {
    display: 'flex',
    gap: '10px',
    marginTop: '8px',
    flexWrap: 'wrap'
  },

  btn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  secondaryBtn: {
    background:
      'transparent',
    color: '#66788a',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  disabledSubmitNavBtn: {
    opacity: 0.45,
    cursor: 'not-allowed'
  },

  dangerBtn: {
    background:
      'transparent',
    color: '#a12a2a',
    border:
      '1px solid #e2b8b8',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  gradeRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap'
  },

  gradeBox: {
    flex: 1,
    minWidth: '140px',
    background: '#f7f9fa',
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px'
  },

  gradeLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a',
    textTransform:
      'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px'
  },

  gradeValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1f2933'
  },

  gradeInput: {
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '18px',
    width: '100%',
    boxSizing: 'border-box',
    background: 'white'
  },

  questionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },

  questionItem: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px',
    background: 'white'
  },

  questionTitle: {
    fontWeight: '700',
    fontSize: '14px',
    color: '#1f2933',
    marginBottom: '4px'
  },

  questionPrompt: {
    fontSize: '13px',
    color: '#66788a',
    marginBottom: '10px'
  },

  answerBox: {
    background: '#f7f9fa',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#1f2933',
    lineHeight: '1.5',
    marginBottom: '10px',
    whiteSpace: 'pre-wrap'
  },

  commentField: {
    marginTop: '10px'
  },

  cqType: {
    display: 'inline-flex',
    marginBottom: '10px',
    padding: '3px 8px',
    borderRadius: '999px',
    background: '#e7f1f7',
    color: '#215b7d',
    fontSize: '11px',
    fontWeight: '700'
  },

  cqQuestionText: {
    whiteSpace: 'pre-wrap',
    fontSize: '15px',
    lineHeight: '1.55',
    color: '#1f2933'
  },

  cqUnsubmitNote: {
    marginTop: '7px',
    fontSize: '11px',
    lineHeight: '1.45',
    color: '#7a8894'
  },

  structuredSnapshot: {
    display: 'grid',
    gap: '12px',
    marginTop: '12px'
  },

  structuredVersionLabel: {
    display: 'grid',
    gap: '6px',
    marginBottom: '14px',
    color: '#334e68',
    fontSize: '12px',
    fontWeight: 700
  },

  structuredVersionSelect: {
    width: '100%',
    padding: '9px 10px',
    border: '1px solid #c7d1db',
    borderRadius: '7px',
    background: 'white',
    color: '#1f2933',
    fontSize: '13px'
  },

  structuredSection: {
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'white'
  },

  structuredSectionInactive: {
    opacity: 0.72
  },

  structuredSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '11px 12px',
    background: '#f7f9fa',
    color: '#1f2933'
  },

  structuredDescription: {
    marginTop: '4px',
    color: '#66788a',
    fontSize: '12px',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap'
  },

  structuredActive: {
    flexShrink: 0,
    borderRadius: '999px',
    padding: '3px 7px',
    background: '#e7f5ed',
    color: '#216e45',
    fontSize: '9px',
    fontWeight: 700
  },

  structuredInactive: {
    flexShrink: 0,
    borderRadius: '999px',
    padding: '3px 7px',
    background: '#edf2f7',
    color: '#66788a',
    fontSize: '9px',
    fontWeight: 700
  },

  structuredField: {
    padding: '12px',
    borderTop: '1px solid #e7ecf0'
  },

  structuredPrompt: {
    color: '#334e68',
    fontSize: '12px',
    fontWeight: 700
  },

  structuredAnswer: {
    marginTop: '8px',
    padding: '10px',
    borderRadius: '6px',
    background: '#f8fafb',
    color: '#1f2933',
    fontSize: '13px',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap'
  }
};
